import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCatalog } from '../../contexts/CatalogContext'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import EditingReturnBanner, { backToLabelFor } from './EditingReturnBanner'
import Card from '../ui/Card'
import Button from '../ui/Button'
import FloorSheetCopyPanel from './FloorSheetCopyPanel'
import PageHeader from '../layout/PageHeader'
import ProjectPageTitle from '../layout/ProjectPageTitle'
import { formatProjectPageSubtitle } from '../../utils/projectSubtitle'
import InlineSelect from '../ui/InlineSelect'
import Combobox from '../ui/Combobox'
import MultiSelect from '../ui/MultiSelect'
import WeldIdCatalogSelect from './weldUpload/WeldIdCatalogSelect'
import SingleSelect, { type SingleSelectHandle } from '../ui/SingleSelect'
import { Cog6ToothIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import {
  measurementColumnBlockedMessage,
  minLockedCount,
  weldTabBlockedMessage
} from '../../utils/collectionSizing'
import { FLOOR_SHEET_DEFAULT_LIMITS } from '../../constants/floorSheetDefaultLimits'
import { formatPreheatBoundDisplay } from '../../utils/formatPreheatBoundDisplay'
import { shouldSkipFloorSheetHydrate } from '../../utils/floorSheetHydrateDirty'
import type { SlopeTimeConfig } from '../../utils/weldSlopeFilter'
import { fetchTeamMembers } from '../../api/teamMembersApi'
import type { TeamMember } from '../../types/teamMember'
import { type ExistingWeldSummary } from './UploadWeldDataDialog'
import UnifiedWeldUploadDialog, {
  telemetryDraftFromPrepared
} from './weldUpload/UnifiedWeldUploadDialog'
import {
  buildAlxWeldParamsSection,
  finalizeAlxUploadForFiles,
  prepareAlxUploadForFiles
} from '../../utils/alx/alxUploadPipeline'
import {
  finalizeTelemetryUploadForFiles,
  prepareTelemetryUploadForFiles,
  buildTelemetryWeldParamsSection
} from '../../utils/weldUpload/telemetryUploadPipeline'
import type { CwCcwMode } from '../../utils/alx/types'
import type { UploadFormat } from '../../utils/weldUpload/types'
import type { WeldSetupBevelOption } from './WeldSetupDialog'
import {
  fetchExistingWeldTelemetry
} from '../../utils/weldTelemetryClient'
import {
  expandPassSequenceToLabels,
  passSectionInputForActiveBevel,
  passSectionInputForBevel
} from '../../utils/passSequenceExpanded'
import {
  evaluateFloorSheetParametersPreflight
} from '../../utils/floorSheetParametersPreflight'
import { evaluateFloorSheetWeldIdPreflight } from '../../utils/floorSheetWeldIdPreflight'
import {
  applyFloorSheetFieldPreflightError,
  type FloorSheetFieldPreflightError,
  type FloorSheetParamPreflightField
} from '../../utils/floorSheetFieldPreflightErrors'
import {
  getData360DistinctPassNames,
  getData360PassMappingRows,
  hasPersistedWeldParams,
  needsData360PassMapping
} from '../../utils/floorSheetWeldParams'
import { normalizeFsExtras, normalizeRevision } from '../../utils/floorSheetFsExtras'
import { normalizeFsTimesLogSnapshot } from '../../utils/deriveFloorSheetFsTimes'
import {
  deriveData360FsTimesFromTelemetry,
  passLabelsForFsTimesImport
} from '../../utils/applyData360FsTimesToFloorsheet'
import {
  emptyDeposition,
  emptyFsHeader,
  emptyFsTargets,
  emptyFsTargetValues,
  emptyFsTimes,
  emptyParamRecord,
  normalizeDeposition,
  normalizeFsHeader,
  normalizeFsTargets,
  normalizeFsTargetValues,
  normalizeFsTimes,
  normalizeParamRecord,
  remapDepositionLocations,
  type FloorSheetDeposition,
  type FloorSheetFsHeader,
  type FloorSheetFsTargetValues,
  type FloorSheetFsTimes,
  type FloorSheetParamRecord,
  type FloorSheetTargets
} from '../../utils/floorSheetCrcFields'
import {
  equipmentListFromRows,
  normalizeOperatorRows,
  syncOperatorMapFromRows,
  syncOperatorRowsForRootEquipment,
  type FloorSheetOperatorRow
} from '../../utils/floorSheetOperatorRows'
import FloorSheetOperatorPanel from './FloorSheetOperatorPanel'
import FloorSheetPassRecordTabStrip from './FloorSheetPassRecordTabStrip'
import FloorSheetRootEquipmentRadios, {
  rootEquipmentRadioLabel,
  type FloorSheetRootEquipment
} from './FloorSheetRootEquipmentRadios'
import FloorSheetIwmRootSwapBanner from './FloorSheetIwmRootSwapBanner'
import { readSwapIwmBugType } from '../../utils/pqr/swapIwmBugType'
import {
  DEPOSITION_TAB,
  FloorSheetCrcTabBody,
  FloorSheetWeldCompletionTimes,
  MISCELLANEOUS_TAB,
  NOTES_TAB,
  OPERATOR_INFORMATION_TAB,
  ROOT_BEAD_TAB,
  ROOT_MISFIRE_TAB,
  WELD_COMPLETION_TIMES_TAB
} from './FloorSheetCrcTabs'
import {
  buildMisfireMetaSeed,
  filterDepositionPassLabels,
  mergeMisfireMetaPrefill,
  passLabelsIncludeRootMisfire
} from '../../utils/floorSheetMisfirePrefill'
import { readClampPowerSupplyExtras } from '../../utils/floorSheetMisfireMetaOptions'
import FloorSheetTargetValuesPanel from './FloorSheetTargetValuesPanel'
import FloorSheetMiscellaneousPanel from './FloorSheetMiscellaneousPanel'
import { resolveBevelGeometrySlots } from '../../utils/bevelGeometryImagesClient'
import {
  applyMiscellaneousDraftToWeldFields,
  applyTargetValuesDraftToDeposition,
  bevelGeometryHintsFromResolve,
  buildMiscellaneousDraft,
  buildTargetValuesDraft,
  draftToFsTargetValues,
  mergeTargetValuesDraftWithPersisted,
  readBevelMaxJointMisalignment,
  type FloorSheetTargetValuesDraft
} from '../../utils/floorSheetTargetValues'
import Data360PassMappingDialog from './weldUpload/Data360PassMappingDialog'
import { useData360FloorSheet } from '../../hooks/useData360FloorSheet'
import {
  decideApplyWeldIdToRow,
  type ApplyWeldIdMode
} from '../../utils/floorSheetApplyWeldId'
import { type FloorSheetCopyPanelWeld } from '../../utils/floorSheetCopy'
import {
  DATA_360_DISPLAY_NAME,
  formatData360DisplayText
} from '../../utils/data360DisplayName'
import ConfirmDialog from '../ui/ConfirmDialog'
const defaultMeasurementLocations = ["12 o'clock", "3 o'clock", "6 o'clock", "9 o'clock"]
const clockLocationOptions = [
  "12 o'clock",
  "1 o'clock",
  "2 o'clock",
  "3 o'clock",
  "4 o'clock",
  "5 o'clock",
  "6 o'clock",
  "7 o'clock",
  "8 o'clock",
  "9 o'clock",
  "10 o'clock",
  "11 o'clock"
]

type PassRecord = {
  passName: string
  /** Same value on every pass for a weld; duplicated in JSON for each pass row. */
  weldId: string
  weldNumber: string
  preheatUnit: '°F' | '°C'
  preheatMeasurements: Record<string, string>
  depositionUnit: 'in' | 'mm'
  depositionMeasurements: Record<string, string>
  bevelUnit: 'in' | 'mm'
  bevelMeasurements: Record<string, string>
}

type WeldOperators = {
  equipment: string[]
  operatorMap: Record<string, { name: string; id: string }>
  rows: FloorSheetOperatorRow[]
  notes: string
}

type BevelRowKey = 'Wall Thickness' | 'Shelf Height' | 'Offset' | 'Land Thickness'
const bevelRowLabels: BevelRowKey[] = ['Wall Thickness', 'Shelf Height', 'Offset', 'Land Thickness']

type BevelMeasurementMatrix = Record<BevelRowKey, Record<string, string>>
type BevelMeasurementsByPup = {
  pupOne: BevelMeasurementMatrix
  pupTwo: BevelMeasurementMatrix
}

type WeldLimitRule = {
  min: string
  max: string
  unit: 'in' | 'mm'
}

type WeldLimitSettings = Record<BevelRowKey, WeldLimitRule>

type PassPreheatInterpassBounds = {
  min: string
  max: string
  /** Arithmetic mean of the recorded per-location temperatures for the pass. */
  avg?: string
}

type PreheatInterpassByPass = Record<string, PassPreheatInterpassBounds>

/**
 * Per-pass, per-circumferential-location recorded preheat/interpass
 * temperature (a single value per location, keyed by `measurementLocations`).
 */
type PreheatInterpassMeasurementsByPass = Record<string, Record<string, string>>

type WeldEntry = {
  id: string
  weldId: string
  weldNumber: string
  sheetName: string
  passRecords: PassRecord[]
  operators: WeldOperators
  measurementLocations: string[]
  pupOne: string
  pupTwo: string
  pupOneSide: string
  pupTwoSide: string
  pupOneSeamLocation: string
  pupTwoSeamLocation: string
  heatNumbers: string[]
  bevelUnit: 'in' | 'mm'
  bevelMeasurementsByPup: BevelMeasurementsByPup
  limitSettings: WeldLimitSettings
  /**
   * Slope-in / slope-out window (in whole seconds) applied at TXT-upload
   * time. The `UploadWeldDataDialog` drops samples whose `recordedAt` falls
   * in the first `inSec` or last `outSec` seconds of the unioned batch
   * before aggregating, so ramp-up / ramp-down noise doesn't pollute the
   * min/avg/max stats. Set per-weld via the Measurement Settings modal.
   */
  slopeTime: SlopeTimeConfig
  /**
   * Per-pass preheat/interpass stats. DERIVED aggregate of
   * `preheatInterpassMeasurementsByPass`: `min`/`max`/`avg` of the recorded
   * per-location temperatures. Kept in sync so downstream analytics/PDF
   * consumers that read a single band per pass keep working.
   */
  preheatInterpassByPass?: PreheatInterpassByPass
  /**
   * Recorded preheat/interpass temperature per pass, per circumferential
   * measurement location (keyed by the same `measurementLocations` used for
   * bevel measurements, e.g. `"12 o'clock"`). Source of truth for the
   * floor-sheet Preheat/Interpass table.
   */
  preheatInterpassMeasurementsByPass?: PreheatInterpassMeasurementsByPass
  preheatInterpassUnit?: '°F' | '°C'
  /**
   * Index of the WPS bevel this weld targets. Captured the first time a user
   * clicks the row's Parameters button (via WeldSetupDialog) so the TXT-pass
   * rename step downstream uses the right pass plan. `null` for legacy rows
   * that predate the per-weld bevel choice.
   */
  bevelIndex: number | null
  /**
   * Root equipment mode for this weld. IWM always drives Root Bead Record;
   * `iwm_and_rc` also shows Root Misfire Record + Semi-Automatic operators.
   */
  rootEquipment: 'iwm' | 'iwm_and_rc'
  data360ProductionWeldId?: number
  data360ProductionId?: number
  /** Floor Sheet PDF revision when imported (e.g. Rev. 0). */
  revision?: string
  /**
   * Leftover Procedure Qualification Floor Sheet blocks (deposition, param
   * grids, comments, header meta) stored on import even without a UI tab.
   * Preserved across save so future tabs / export can use them.
   */
  fsExtras?: Record<string, unknown>
  fsHeader: FloorSheetFsHeader
  fsTimes: FloorSheetFsTimes
  /** Populated when times were derived from Data360 / telemetry logs. */
  fsTimesSource?: 'manual' | 'data360-logs' | null
  /** Snapshot of log-derived ISO timestamps for deviation warnings. */
  fsTimesLogSnapshot?: {
    derivedAt: string
    rootCompleteAt: string | null
    hotPassStartAt: string | null
    weldCompletedAt: string | null
    counts: { root: number; hot: number; all: number }
    timezone: 'UTC'
  } | null
  shopFloorComments: string
  deposition: FloorSheetDeposition
  paramRecord: FloorSheetParamRecord
  fsTargets: FloorSheetTargets
  fsTargetValues: FloorSheetFsTargetValues
}

type StoredCustomPass = { name?: unknown; count?: unknown } | string
type StoredWeldEntry = Partial<WeldEntry> & { [key: string]: unknown }

type FloorSheetStored = {
  welds?: StoredWeldEntry[]
  weldNumber?: string
  sheetName?: string
  passRecords?: PassRecord[]
  operators?: WeldOperators
}

const createPositionMap = (locations: string[], value = '') =>
  locations.reduce((acc, position) => {
    acc[position] = value
    return acc
  }, {} as Record<string, string>)

const createBevelMatrix = (locations: string[]) =>
  bevelRowLabels.reduce((acc, row) => {
    acc[row] = createPositionMap(locations)
    return acc
  }, {} as BevelMeasurementMatrix)

const defaultLimitRuleForRow = (row: BevelRowKey, unit: 'in' | 'mm'): WeldLimitRule => {
  const d = FLOOR_SHEET_DEFAULT_LIMITS[row]
  return { min: d.min, max: d.max, unit: unit === 'mm' ? 'mm' : d.unit }
}

const createDefaultLimitSettings = (unit: 'in' | 'mm' = 'in'): WeldLimitSettings =>
  bevelRowLabels.reduce((acc, row) => {
    acc[row] = defaultLimitRuleForRow(row, unit)
    return acc
  }, {} as WeldLimitSettings)

/**
 * Default slope window used when a weld has never had its Slope Time tab
 * touched. Four seconds in / four seconds out matches the welding-floor
 * heuristic for trimming ramp-up and ramp-down on a typical pass.
 */
const DEFAULT_SLOPE_TIME: SlopeTimeConfig = { inSec: 0, outSec: 0 }

const BEVEL_MEASUREMENTS_TAB_LABEL = 'Bevel Measurements'
const PREHEAT_INTERPASS_TAB_LABEL = 'Preheat / Interpass'

/** Section tabs in the pass-record area; legacy pass names (Root, Hot, …) are not included. */
const VALID_PASS_RECORD_TAB_LABELS = new Set([
  BEVEL_MEASUREMENTS_TAB_LABEL,
  PREHEAT_INTERPASS_TAB_LABEL,
  DEPOSITION_TAB,
  ROOT_BEAD_TAB,
  ROOT_MISFIRE_TAB,
  WELD_COMPLETION_TIMES_TAB,
  OPERATOR_INFORMATION_TAB,
  NOTES_TAB,
  MISCELLANEOUS_TAB
])

function canAccessPreheatTab(
  weld: Pick<WeldEntry, 'pupOne' | 'pupTwo' | 'bevelIndex'>
): boolean {
  return Boolean(weld.pupOne.trim() && weld.pupTwo.trim() && weld.bevelIndex !== null)
}

function defaultPreheatInterpassUnit(
  preheatSection: Record<string, unknown> | null | undefined
): '°F' | '°C' {
  const unit = String(preheatSection?.preheatUnit ?? preheatSection?.interpassUnit ?? '°F').trim()
  return unit === '°C' ? '°C' : '°F'
}

function normalizePreheatInterpassByPass(raw: unknown): PreheatInterpassByPass | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const out: PreheatInterpassByPass = {}
  for (const [pass, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue
    const row = value as { min?: unknown; max?: unknown; avg?: unknown }
    out[pass] = {
      min: String(row.min ?? ''),
      max: String(row.max ?? ''),
      avg: String(row.avg ?? '')
    }
  }
  return Object.keys(out).length > 0 ? out : undefined
}

/**
 * Coerce a persisted per-pass / per-location recorded-temperature map into a
 * `Record<pass, Record<location, string>>`, keeping only the supplied
 * `locations` (so location edits stay in sync with the bevel table).
 */
function normalizePreheatInterpassMeasurementsByPass(
  raw: unknown,
  locations: string[]
): PreheatInterpassMeasurementsByPass {
  const out: PreheatInterpassMeasurementsByPass = {}
  if (!raw || typeof raw !== 'object') return out
  for (const [pass, value] of Object.entries(raw as Record<string, unknown>)) {
    const byLocation = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
    out[pass] = locations.reduce((acc, location) => {
      const cell = byLocation[location]
      acc[location] = typeof cell === 'string' || typeof cell === 'number' ? String(cell) : ''
      return acc
    }, {} as Record<string, string>)
  }
  return out
}

/**
 * Ensure every pass/location cell exists in the recorded-temperature map,
 * preserving any existing value and defaulting empty cells to a blank string
 * (no pre-filled defaults).
 */
function ensurePreheatInterpassMeasurementsByPass(
  weld: Pick<WeldEntry, 'preheatInterpassMeasurementsByPass'>,
  passLabels: string[],
  locations: string[]
): PreheatInterpassMeasurementsByPass {
  const existing = weld.preheatInterpassMeasurementsByPass ?? {}
  const out: PreheatInterpassMeasurementsByPass = {}
  for (const pass of passLabels) {
    const existingPass = existing[pass] ?? {}
    out[pass] = locations.reduce((acc, location) => {
      const cell = existingPass[location]
      acc[location] = typeof cell === 'string' && cell.trim() ? cell : ''
      return acc
    }, {} as Record<string, string>)
  }
  return out
}

/**
 * Collapse the recorded per-location temperatures into the single per-pass
 * stats consumed by downstream analytics/PDF: `min`/`max`/`avg` (arithmetic
 * mean) of the recorded values; blank cells are ignored.
 */
function aggregatePreheatInterpassByPass(
  measurementsByPass: PreheatInterpassMeasurementsByPass
): PreheatInterpassByPass {
  const out: PreheatInterpassByPass = {}
  for (const [pass, byLocation] of Object.entries(measurementsByPass)) {
    const values: number[] = []
    for (const cell of Object.values(byLocation ?? {})) {
      const value = parseOptionalNumber(cell)
      if (value !== null) values.push(value)
    }
    if (values.length === 0) {
      out[pass] = { min: '', max: '', avg: '' }
      continue
    }
    const sum = values.reduce((acc, value) => acc + value, 0)
    const mean = sum / values.length
    out[pass] = {
      min: String(Math.min(...values)),
      max: String(Math.max(...values)),
      avg: String(Number(mean.toFixed(2)))
    }
  }
  return out
}

const createDefaultSlopeTime = (): SlopeTimeConfig => ({ ...DEFAULT_SLOPE_TIME })

/** Clamp persisted slope-time values back to non-negative whole seconds. */
const normalizeSlopeTime = (value: unknown): SlopeTimeConfig => {
  if (!value || typeof value !== 'object') return createDefaultSlopeTime()
  const typed = value as Record<string, unknown>
  const inSec = sanitizeSlopeSec(typed.inSec, DEFAULT_SLOPE_TIME.inSec)
  const outSec = sanitizeSlopeSec(typed.outSec, DEFAULT_SLOPE_TIME.outSec)
  return { inSec, outSec }
}

function sanitizeSlopeSec(raw: unknown, fallback: number): number {
  if (raw === undefined || raw === null || raw === '') return fallback
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 0) return fallback
  return Math.floor(n)
}

/** Parse numeric threshold only when the field was explicitly set (avoids Number('') === 0). */
function parseOptionalNumber(raw: string | undefined): number | null {
  const t = String(raw ?? '').trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

/** Legacy limitSettings used `warning` / `limit` as >= thresholds; map to min/max band. */
const normalizeLimitSettings = (value: unknown, fallbackUnit: 'in' | 'mm'): WeldLimitSettings => {
  const defaults = createDefaultLimitSettings(fallbackUnit)
  if (!value || typeof value !== 'object') return defaults
  const next: WeldLimitSettings = { ...defaults }
  bevelRowLabels.forEach((row) => {
    const source = (value as Record<string, unknown>)[row]
    if (!source || typeof source !== 'object') {
      next[row] = defaults[row]
      return
    }
    const typed = source as Record<string, unknown>
    let min = String(typed.min ?? '')
    let max = String(typed.max ?? '')
    if (!String(min).trim() && !String(max).trim() && (typed.limit != null || typed.warning != null)) {
      min = String(typed.warning ?? '')
      max = String(typed.limit ?? '')
    }
    if (!String(min).trim() && !String(max).trim()) {
      next[row] = defaults[row]
      return
    }
    next[row] = {
      min,
      max,
      unit: typed.unit === 'mm' ? 'mm' : fallbackUnit
    }
  })
  return next
}

type MeasurementLimitBand = 'ok' | 'belowMin' | 'aboveMax'

function measurementLimitBand(valueStr: string, rule: WeldLimitRule): MeasurementLimitBand {
  const v = parseOptionalNumber(valueStr)
  if (v === null) return 'ok'
  const minN = parseOptionalNumber(rule.min)
  const maxN = parseOptionalNumber(rule.max)
  if (minN !== null && maxN !== null) {
    if (minN > maxN) return 'ok'
    if (v < minN) return 'belowMin'
    if (v > maxN) return 'aboveMax'
    return 'ok'
  }
  if (maxN !== null && v > maxN) return 'aboveMax'
  if (minN !== null && v < minN) return 'belowMin'
  return 'ok'
}

function limitInputClassName(valueStr: string, rule: WeldLimitRule): string {
  const band = measurementLimitBand(valueStr, rule)
  if (band === 'belowMin') return 'border-amber-300 bg-amber-50'
  if (band === 'aboveMax') return 'border-error-400 bg-error-50'
  return 'border-gray-300 bg-white'
}

function limitStatValueClass(n: number | null, rule: WeldLimitRule): string {
  if (n === null) return ''
  const band = measurementLimitBand(String(n), rule)
  if (band === 'belowMin') return 'text-amber-600 font-semibold'
  if (band === 'aboveMax') return 'text-error-600 font-semibold'
  return ''
}

/** Closed Pup selector: `Heat (Desc..)` — description truncated after 6 chars. */
function formatPupTriggerLabel(heat: string, description: string | undefined): string {
  const h = String(heat || '').trim()
  if (!h) return ''
  const d = String(description ?? '').trim()
  if (!d) return h
  const short = d.length > 6 ? `${d.slice(0, 6)}..` : d
  return `${h} (${short})`
}

function isFloorsheetSectionRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

const getBevelStats = (matrix: BevelMeasurementsByPup, rows: BevelRowKey[], locations: string[]) =>
  rows.reduce((acc, row) => {
    const values = [
      ...locations.map((location) => Number(matrix.pupOne[row][location])),
      ...locations.map((location) => Number(matrix.pupTwo[row][location]))
    ].filter((value) => !Number.isNaN(value))
    const sum = values.reduce((total, value) => total + value, 0)
    acc[row] = {
      min: values.length ? Math.min(...values) : null,
      max: values.length ? Math.max(...values) : null,
      avg: values.length ? sum / values.length : null
    }
    return acc
  }, {} as Record<BevelRowKey, { min: number | null; max: number | null; avg: number | null }>)

function columnKeyForLocation(locations: string[], index: number): string {
  const t = (locations[index] ?? '').trim()
  return t || `Location ${index + 1}`
}

function columnIndexHasData(weld: WeldEntry, colIndex: number, locations: string[]): boolean {
  const key = columnKeyForLocation(locations, colIndex)
  for (const row of bevelRowLabels) {
    if (String(weld.bevelMeasurementsByPup.pupOne[row]?.[key] ?? '').trim()) return true
    if (String(weld.bevelMeasurementsByPup.pupTwo[row]?.[key] ?? '').trim()) return true
  }
  for (const pr of weld.passRecords) {
    if (String(pr.preheatMeasurements[key] ?? '').trim()) return true
    if (String(pr.depositionMeasurements[key] ?? '').trim()) return true
    if (String(pr.bevelMeasurements[key] ?? '').trim()) return true
  }
  return false
}

function minLockedMeasurementColumns(weld: WeldEntry, locations: string[]): number {
  let m = 0
  for (let i = 0; i < locations.length; i++) {
    if (columnIndexHasData(weld, i, locations)) m = i + 1
  }
  return m
}

function computeResizedSettingsLocations(
  prev: string[],
  clamped: number,
  targetWeld: WeldEntry | undefined
): { next: string[]; error: string | null } {
  if (clamped === prev.length) return { next: prev, error: null }
  if (clamped < prev.length && targetWeld) {
    const locked = minLockedMeasurementColumns(targetWeld, prev)
    if (clamped < locked) return { next: prev, error: measurementColumnBlockedMessage(locked) }
  }
  if (clamped < prev.length) return { next: prev.slice(0, clamped), error: null }
  return {
    next: [...prev, ...Array.from({ length: clamped - prev.length }, () => '')],
    error: null
  }
}

function weldEntryOccupied(weld: WeldEntry): boolean {
  if (weld.weldId.trim() || weld.weldNumber.trim() || weld.sheetName.trim()) return true
  if (weld.pupOne.trim() || weld.pupTwo.trim()) return true
  if (weld.heatNumbers.some((h) => String(h).trim())) return true
  if (weld.operators.equipment.length || weld.operators.notes.trim()) return true
  if (Object.values(weld.operators.operatorMap).some((o) => o.name.trim() || o.id.trim())) return true
  return minLockedMeasurementColumns(weld, weld.measurementLocations) > 0
}

const getRowStats = (valuesMap: Record<string, string>, locations: string[]) => {
  const values = locations
    .map((location) => Number(valuesMap[location]))
    .filter((value) => !Number.isNaN(value))
  if (!values.length) {
    return { min: null, max: null, avg: null }
  }
  const sum = values.reduce((total, value) => total + value, 0)
  return {
    min: Math.min(...values),
    max: Math.max(...values),
    avg: sum / values.length
  }
}

const createEmptyPassRecord = (
  passName: string,
  locations: string[],
  weldId = '',
  weldNumber = ''
): PassRecord => ({
  passName,
  weldId,
  weldNumber,
  preheatUnit: '°F',
  preheatMeasurements: createPositionMap(locations),
  depositionUnit: 'in',
  depositionMeasurements: createPositionMap(locations),
  bevelUnit: 'in',
  bevelMeasurements: createPositionMap(locations)
})

const normalizePassRecord = (record: any, locations: string[]): PassRecord => {
  const normalizeTempUnit = (value: string | undefined): '°F' | '°C' =>
    value === '°C' ? '°C' : '°F'
  const normalizeLengthUnit = (value: string | undefined): 'in' | 'mm' =>
    value === 'mm' ? 'mm' : 'in'
  const preheatMeasurements = record?.preheatMeasurements && typeof record.preheatMeasurements === 'object'
    ? locations.reduce((acc, position) => {
        acc[position] = String(record.preheatMeasurements[position] ?? '')
        return acc
      }, {} as Record<string, string>)
    : createPositionMap(locations, record?.preheatTemp || '')
  const depositionMeasurements = record?.depositionMeasurements && typeof record.depositionMeasurements === 'object'
    ? locations.reduce((acc, position) => {
        acc[position] = String(record.depositionMeasurements[position] ?? '')
        return acc
      }, {} as Record<string, string>)
    : createPositionMap(locations, record?.depositionDepth || '')
  const bevelMeasurements = record?.bevelMeasurements && typeof record.bevelMeasurements === 'object'
    ? locations.reduce((acc, position) => {
        acc[position] = String(record.bevelMeasurements[position] ?? '')
        return acc
      }, {} as Record<string, string>)
    : createPositionMap(locations, record?.bevelMeasurement || '')

  return {
    passName: record?.passName || '',
    weldId: String(record?.weldId ?? ''),
    weldNumber: String(record?.weldNumber ?? ''),
    preheatUnit: normalizeTempUnit(record?.preheatUnit),
    preheatMeasurements,
    depositionUnit: normalizeLengthUnit(record?.depositionUnit),
    depositionMeasurements,
    bevelUnit: normalizeLengthUnit(record?.bevelUnit),
    bevelMeasurements
  }
}

function resolveWeldIdentityFromRecords(
  weld: { weldId?: unknown; weldNumber?: unknown },
  passRecords: PassRecord[]
): { weldId: string; weldNumber: string } {
  const fromPr = passRecords.find((r) => r.weldId.trim() || r.weldNumber.trim())
  const weldId = String(weld.weldId || weld.weldNumber || fromPr?.weldId || fromPr?.weldNumber || '')
  const weldNumber = String(weld.weldNumber || weld.weldId || fromPr?.weldNumber || fromPr?.weldId || '')
  return { weldId, weldNumber }
}

function syncPassRecordsWeldIdentity(passRecords: PassRecord[], weldId: string, weldNumber: string): PassRecord[] {
  return passRecords.map((r) => ({ ...r, weldId, weldNumber }))
}

const ensurePassRecords = (
  records: PassRecord[],
  passList: string[],
  locations: string[],
  weldId: string,
  weldNumber: string
) => {
  const map = new Map(records.map((record) => [record.passName, record]))
  return passList.map((passName) => {
    const existing = map.get(passName)
    const base = existing || createEmptyPassRecord(passName, locations, weldId, weldNumber)
    return { ...base, weldId, weldNumber }
  })
}

/** Deep copy for API persistence: each pass row carries the weld's id fields. */
function weldsForFloorsheetPersistence(welds: WeldEntry[]): WeldEntry[] {
  return welds.map((w) => ({
    ...w,
    passRecords: w.passRecords.map((r) => ({
      ...r,
      weldId: w.weldId,
      weldNumber: w.weldNumber
    }))
  }))
}

const createWeldEntry = (index: number): WeldEntry => ({
  id: `weld-${Date.now()}-${index}-${Math.random().toString(16).slice(2, 6)}`,
  weldId: '',
  weldNumber: '',
  sheetName: '',
  passRecords: [],
  operators: {
    equipment: [],
    operatorMap: {},
    rows: [],
    notes: ''
  },
  measurementLocations: [...defaultMeasurementLocations],
  pupOne: '',
  pupTwo: '',
  pupOneSide: '',
  pupTwoSide: '',
  pupOneSeamLocation: '',
  pupTwoSeamLocation: '',
  heatNumbers: [],
  bevelUnit: 'in',
  bevelMeasurementsByPup: {
    pupOne: createBevelMatrix(defaultMeasurementLocations),
    pupTwo: createBevelMatrix(defaultMeasurementLocations)
  },
  limitSettings: createDefaultLimitSettings('in'),
  slopeTime: createDefaultSlopeTime(),
  bevelIndex: null,
  rootEquipment: 'iwm',
  preheatInterpassByPass: {},
  preheatInterpassMeasurementsByPass: {},
  preheatInterpassUnit: '°F',
  fsHeader: emptyFsHeader(),
  fsTimes: emptyFsTimes(),
  shopFloorComments: '',
  deposition: emptyDeposition(defaultMeasurementLocations),
  paramRecord: emptyParamRecord(),
  fsTargets: emptyFsTargets(),
  fsTargetValues: emptyFsTargetValues()
})

/** Normalize a persisted `bevelIndex` value back into the typed shape. */
function normalizeBevelIndex(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
    return Math.trunc(value)
  }
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    const n = Math.trunc(Number(value))
    return n >= 0 ? n : null
  }
  return null
}

function normalizeRootEquipment(
  value: unknown,
  legacyHasRootMisfire: boolean
): 'iwm' | 'iwm_and_rc' {
  if (value === 'iwm_and_rc' || value === 'iwm') return value
  return legacyHasRootMisfire ? 'iwm_and_rc' : 'iwm'
}

type FloorSheetProceedAction =
  | { kind: 'weldTab'; weldId: string }
  | { kind: 'navigate'; to: string }

export default function FloorSheet() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { getProject } = useProjects()
  const { getLabelsMerged, addLabel } = useCatalog()
  const { getSection, updateSection, sections, refresh, syncSectionsVersion } = useWpsSections()
  const [uploadDialogWeldId, setUploadDialogWeldId] = useState<string | null>(null)
  const [uploadDialogExistingSummary, setUploadDialogExistingSummary] =
    useState<ExistingWeldSummary | null>(null)
  // Pass labels expanded per WPS bevel; `null` means "use the page's active
  // bevel" (legacy / pre-setup welds). The Parameters button passes the
  // chosen bevel index through this when opening the upload dialog.
  const [uploadDialogBevelIndex, setUploadDialogBevelIndex] = useState<number | null>(null)
  const batchWeldsRef = useRef<{ nextWelds: WeldEntry[]; cwCcwMode: CwCcwMode } | null>(null)

  const passLabelsForBevel = useCallback(
    (bevelIndex: number | null): string[] => {
      const section = getSection('pass')
      const input =
        bevelIndex === null
          ? passSectionInputForActiveBevel(section)
          : passSectionInputForBevel(section, bevelIndex)
      return expandPassSequenceToLabels(input)
    },
    [getSection]
  )
  const uploadDialogPassLabels = useMemo(
    () => passLabelsForBevel(uploadDialogBevelIndex),
    [passLabelsForBevel, uploadDialogBevelIndex]
  )

  const wpsBevelOptions: WeldSetupBevelOption[] = useMemo(() => {
    const section = getSection('bevel') as { bevels?: unknown } | null | undefined
    const list = Array.isArray(section?.bevels) ? section!.bevels : []
    return list.map((entry, index) => {
      const jointTypeRaw = (entry as { jointType?: unknown })?.jointType
      const jointType = Array.isArray(jointTypeRaw)
        ? jointTypeRaw.filter((value): value is string => typeof value === 'string' && value.trim() !== '')
        : []
      const suffix = jointType.length > 0 ? ` — ${jointType.join(', ')}` : ''
      return {
        index,
        label: `Bevel ${index + 1}${suffix}`,
        jointType
      }
    })
  }, [getSection])
  // The `returnToParam` is read further down to render the PqrReturnBanner
  // and rewire the page-header back button.
  const [searchParams, setSearchParams] = useSearchParams()
  const returnToParam = searchParams.get('returnTo')
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)
  const data360 = useData360FloorSheet(id, pipeId, project?.data360ProjectId)
  const [importDialog, setImportDialog] = useState<{
    weldRowId: string
    weldName: string
    productionWeldId: number
    jobNumber?: string
    replaceExisting: boolean
  } | null>(null)
  const [rootEquipmentChangeDialog, setRootEquipmentChangeDialog] = useState<{
    weldId: string
    pending: FloorSheetRootEquipment
  } | null>(null)
  const [data360RenameDialog, setData360RenameDialog] = useState<{ weldRowId: string } | null>(
    null
  )
  const [fsTimesImportBusyWeldId, setFsTimesImportBusyWeldId] = useState<string | null>(null)
  const [syncingWeldRowId, setSyncingWeldRowId] = useState<string | null>(null)
  const [wpsNumber, setWpsNumber] = useState('')
  const [savedWeldsJson, setSavedWeldsJson] = useState('')
  const [saving, setSaving] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  const [welds, setWelds] = useState<WeldEntry[]>([])
  const [activePassByWeld, setActivePassByWeld] = useState<Record<string, string>>({})
  const [activeWeldId, setActiveWeldId] = useState<string | null>(null)
  // Parameters-button preflight: tracks which mandatory field (Weld ID / Pup 1 / Pup 2)
  // is missing for each weld so we can surface an inline red helper next to that field.
  // The Parameters click handler also fires a sonner toast and programmatically opens
  // the offending SingleSelect dropdown via the refs below.
  const [paramErrorByWeld, setParamErrorByWeld] = useState<
    Record<string, { field: FloorSheetParamPreflightField; message: string } | null>
  >({})
  const weldIdSelectRef = useRef<SingleSelectHandle>(null)
  const pupOneSelectRef = useRef<SingleSelectHandle>(null)
  const pupTwoSelectRef = useRef<SingleSelectHandle>(null)

  const applyFieldPreflightError = useCallback(
    (
      weldRowId: string,
      error: FloorSheetFieldPreflightError,
      context: 'parameters' | 'weldId'
    ) => {
      applyFloorSheetFieldPreflightError({
        weldRowId,
        error,
        context,
        bevelMeasurementsTabLabel: BEVEL_MEASUREMENTS_TAB_LABEL,
        setParamErrorByWeld,
        setActivePassByWeld,
        pupOneSelectRef,
        pupTwoSelectRef
      })
    },
    []
  )

  // Legacy pass-tab names (Root, Hot, etc.) are no longer valid — reset to Bevel.
  useEffect(() => {
    setActivePassByWeld((prev) => {
      let changed = false
      const next = { ...prev }
      for (const weld of welds) {
        const active = prev[weld.id]
        if (active && !VALID_PASS_RECORD_TAB_LABELS.has(active)) {
          next[weld.id] = BEVEL_MEASUREMENTS_TAB_LABEL
          changed = true
        }
      }
      return changed ? next : prev
    })
  }, [welds])

  // PQR Preview field-link integration (slice 01 of pqr-preview-field-navigation).
  // When the user clicks "IWM Operator" (or any other welder row) on the PQR
  // Preview, they land here with `?focusWeldId=…&focusField=operatorMap.<key>
  // &returnTo=…`. The hook:
  //   1. Switches the active weld tab to `focusWeldId` (via `onBeforeFocus`)
  //      so the operator field is rendered into the DOM.
  //   2. When `focusPass` is set (Floor Sheet preview section links), selects
  //      the matching CRC tab so the panel content is visible.
  //   3. When `focusField=settings.targets`, opens Measurement Settings on
  //      the Target Values tab (`openSettingsRef` assigned after `openSettings`).
  //   4. Resolves the operator/tab cell by its `data-focus-field` attribute and
  //      focuses the first interactive descendant.
  //   5. Flashes the cell for ~2.5s and strips the focus params.
  const openSettingsRef = useRef<
    ((weld: WeldEntry, initialTab?: 'layout' | 'targets' | 'limits' | 'slope') => void) | null
  >(null)
  useFieldFocusHandler({
    ready: welds.length > 0,
    onBeforeFocus: (params) => {
      let matching: WeldEntry | undefined
      if (params.focusWeldId) {
        matching = welds.find((w) => (w.weldId || w.id) === params.focusWeldId)
        if (matching) setActiveWeldId(matching.id)
      }
      if (params.focusField === 'settings.targets' && matching) {
        openSettingsRef.current?.(matching, 'targets')
        return
      }
      const inferredPass =
        params.focusPass && VALID_PASS_RECORD_TAB_LABELS.has(params.focusPass)
          ? params.focusPass
          : params.focusField === 'operators.notes'
            ? NOTES_TAB
            : params.focusField?.startsWith('fsTimes.')
              ? WELD_COMPLETION_TIMES_TAB
              : params.focusField?.startsWith('operatorMap.')
                ? OPERATOR_INFORMATION_TAB
                : null
      if (inferredPass && VALID_PASS_RECORD_TAB_LABELS.has(inferredPass)) {
        const weldKey = matching?.id ?? activeWeldId
        if (weldKey) {
          setActivePassByWeld((prev) => ({
            ...prev,
            [weldKey]: inferredPass
          }))
        }
      }
    },
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const safeWeldId = params.focusWeldId
        ? params.focusWeldId.replace(/"/g, '\\"')
        : ''
      const safeField = params.focusField.replace(/"/g, '\\"')
      const wrapper = safeWeldId
        ? (document.querySelector(
            `[data-focus-weld-id="${safeWeldId}"][data-focus-field="${safeField}"]`
          ) as HTMLElement | null)
        : (document.querySelector(
            `[data-focus-field="${safeField}"]`
          ) as HTMLElement | null)
      if (!wrapper) return null
      const focusable = wrapper.querySelector(
        'button, [role="button"], input, select, textarea, a[href]'
      ) as HTMLElement | null
      return focusable ?? wrapper
    }
  })
  const [settingsWeldId, setSettingsWeldId] = useState<string | null>(null)
  const [settingsTab, setSettingsTab] = useState<'layout' | 'targets' | 'limits' | 'slope'>('layout')
  const [settingsLocations, setSettingsLocations] = useState<string[]>([])
  const [targetValuesDraft, setTargetValuesDraft] = useState<FloorSheetTargetValuesDraft>({
    unit: 'in',
    calculatedTopOfGroove: '',
    fillAngleA: '',
    shelfHeight: '',
    landThickness: '',
    offsetE: ''
  })
  const [targetBevelAutoFilled, setTargetBevelAutoFilled] = useState<
    Partial<Record<'fillAngleA' | 'offsetE' | 'shelfHeight' | 'landThickness', boolean>>
  >({})
  const [limitSettingsDraft, setLimitSettingsDraft] = useState<WeldLimitSettings>(createDefaultLimitSettings('in'))
  const [slopeTimeDraft, setSlopeTimeDraft] = useState<{ inSec: string; outSec: string }>(
    () => ({
      inSec: String(DEFAULT_SLOPE_TIME.inSec),
      outSec: String(DEFAULT_SLOPE_TIME.outSec)
    })
  )
  const [settingsSourceIndices, setSettingsSourceIndices] = useState<number[]>([])
  const settingsInitialSnapshotRef = useRef<string[]>([])
  const [settingsMeasurementCountDraft, setSettingsMeasurementCountDraft] = useState('4')
  const settingsMeasurementCountRef = useRef<HTMLInputElement | null>(null)
  const [weldCountDraft, setWeldCountDraft] = useState('1')
  const weldCountInputRef = useRef<HTMLInputElement | null>(null)
  const [weldCountError, setWeldCountError] = useState<string | null>(null)
  const [settingsLayoutError, setSettingsLayoutError] = useState<string | null>(null)
  // equipmentOptions removed — currently unused
  const [teamMembersForOperators, setTeamMembersForOperators] = useState<TeamMember[]>([])
  const [teamMembersLoading, setTeamMembersLoading] = useState(false)

  const isDirty = useMemo(
    () => savedWeldsJson !== '' && JSON.stringify(welds) !== savedWeldsJson,
    [welds, savedWeldsJson]
  )
  // Hydrate reads these refs so a 409→reload of `sections` does not wipe a
  // local draft (e.g. Weld ID just typed) the way PQR keeps staged edits.
  const weldsRef = useRef(welds)
  weldsRef.current = welds
  const savedWeldsJsonRef = useRef(savedWeldsJson)
  savedWeldsJsonRef.current = savedWeldsJson
  const hydratePipeIdRef = useRef<string | null>(null)

  const switchToWeldTab = useCallback(
    (weldId: string) => {
      setActiveWeldId(weldId)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('weldTab', weldId)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const persistFloorSheet = useCallback(async (): Promise<boolean> => {
    if (!pipeId) return false
    setSaving(true)
    try {
      const existing = getSection('floorsheet')
      await updateSection('floorsheet', {
        ...(isFloorsheetSectionRecord(existing) ? existing : {}),
        preheatDefaultsPurged: true,
        welds: weldsForFloorsheetPersistence(welds)
      })
      setSavedWeldsJson(JSON.stringify(welds))
      return true
    } catch {
      return false
    } finally {
      setSaving(false)
    }
  }, [pipeId, getSection, updateSection, welds])

  const proceedAfterAutoSave = useCallback(
    async (action: FloorSheetProceedAction) => {
      if (isDirty) {
        const ok = await persistFloorSheet()
        if (!ok) return
      }
      if (action.kind === 'weldTab') {
        switchToWeldTab(action.weldId)
      } else {
        navigate(action.to)
      }
    },
    [isDirty, persistFloorSheet, switchToWeldTab, navigate]
  )

  const projectEquipmentList = useMemo(() => project?.equipment ?? [], [project?.equipment])

  const memberById = useMemo(
    () => new Map(teamMembersForOperators.map((m) => [m.id, m])),
    [teamMembersForOperators]
  )

  const misfirePowerSupplyOptions = useMemo(
    () => readClampPowerSupplyExtras(sections.clamp),
    [sections.clamp]
  )

  const refreshTeamMembersForOperators = useCallback(async (): Promise<TeamMember[]> => {
    if (!id) {
      setTeamMembersForOperators([])
      return []
    }
    const pageSize = 200
    const all: TeamMember[] = []
    let page = 1
    for (;;) {
      const res = await fetchTeamMembers(id, { page, pageSize, status: 'active' })
      all.push(...res.items)
      if (all.length >= res.total || res.items.length === 0) break
      page += 1
      if (page > 500) break
    }
    setTeamMembersForOperators(all)
    return all
  }, [id])

  const importFsTimesFromData360 = useCallback(
    async (weldRowId: string) => {
      if (!id || !pipeId) {
        toast.error('Project context is missing')
        return
      }
      const passLabels = passLabelsForFsTimesImport(getSection, weldRowId)
      if (passLabels.length === 0) {
        toast.error('Import weld log data and map passes before importing completion times.')
        return
      }
      setFsTimesImportBusyWeldId(weldRowId)
      try {
        const derived = await deriveData360FsTimesFromTelemetry({
          projectId: id,
          pipeSetId: pipeId,
          weldRowId,
          passLabels
        })
        if (!derived) {
          toast.error('No timestamps found in Data360 weld logs for this weld.')
          return
        }
        setWelds((prev) =>
          prev.map((item) =>
            item.id === weldRowId
              ? {
                  ...item,
                  fsTimes: derived.fsTimes,
                  fsTimesSource: 'data360-logs' as const,
                  fsTimesLogSnapshot: derived.snapshot
                }
              : item
          )
        )
        toast.success('Weld completion times imported from Data360 logs.')
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to import completion times')
      } finally {
        setFsTimesImportBusyWeldId(null)
      }
    },
    [id, pipeId, getSection]
  )

  useEffect(() => {
    if (!id) {
      setTeamMembersForOperators([])
      setTeamMembersLoading(false)
      return
    }
    let cancelled = false
    setTeamMembersLoading(true)
    ;(async () => {
      try {
        const all = await refreshTeamMembersForOperators()
        if (cancelled) return
        setTeamMembersForOperators(all)
      } catch (e: unknown) {
        if (!cancelled) {
          toast.error(e instanceof Error ? e.message : 'Failed to load team members')
          setTeamMembersForOperators([])
        }
      } finally {
        if (!cancelled) setTeamMembersLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [id, refreshTeamMembersForOperators])

  useEffect(() => {
    setSavedWeldsJson('')
  }, [pipeId])

  useEffect(() => {
    if (!uploadDialogWeldId || !id || !pipeId) {
      setUploadDialogExistingSummary(null)
      return
    }
    let cancelled = false
    fetchExistingWeldTelemetry({
      projectId: id,
      pipeSetId: pipeId,
      weldId: uploadDialogWeldId
    })
      .then((result) => {
        if (cancelled) return
        if (!result.existingUpload && !result.source) {
          setUploadDialogExistingSummary(null)
          return
        }
        setUploadDialogExistingSummary({
          source: result.source ?? 'upload',
          sourceFilename: result.existingUpload?.sourceFilename,
          uploadedAt: result.existingUpload?.uploadedAt,
          passMappings: result.existingUpload?.passMappings
        })
      })
      .catch(() => {
        // Graceful degradation: if the existing-data probe fails, fall back to
        // first-upload mode (no warning banner, no confirm). User loses the
        // safety net but the upload itself stays available.
        if (!cancelled) setUploadDialogExistingSummary(null)
      })
    return () => {
      cancelled = true
    }
  }, [uploadDialogWeldId, id, pipeId])

  useEffect(() => {
    if (savedWeldsJson !== '' || welds.length === 0) return
    setSavedWeldsJson(JSON.stringify(welds))
  }, [welds, savedWeldsJson])

  useEffect(() => {
    if (!pipeId) return
    const metaData = getSection('meta')
    if (metaData) {
      setWpsNumber(metaData.wpsNumber || '')
    }

    const floorData = getSection('floorsheet')
    if (floorData) {
      try {
        const parsed = floorData as FloorSheetStored
        const pipeChanged = hydratePipeIdRef.current !== pipeId
        hydratePipeIdRef.current = pipeId
        // Keep unsaved Weld ID / row edits across conflict reloads of sections.
        if (
          shouldSkipFloorSheetHydrate({
            pipeChanged,
            savedWeldsJson: savedWeldsJsonRef.current,
            welds: weldsRef.current
          })
        ) {
          return
        }
        // Prefer imported / saved clock temps when present. Legacy midpoint
        // defaults are only dropped when the section has no real measurements
        // and preheatDefaultsPurged is still unset.
        const storedHasPreheat = (parsed.welds || []).some((weld) => {
          const byPass = (weld as StoredWeldEntry).preheatInterpassMeasurementsByPass
          if (!byPass || typeof byPass !== 'object') return false
          return Object.values(byPass).some(
            (locs) =>
              locs &&
              typeof locs === 'object' &&
              Object.values(locs as Record<string, unknown>).some(
                (v) => String(v ?? '').trim() !== ''
              )
          )
        })
        const preheatDefaultsPurged =
          Boolean(
            (parsed as { preheatDefaultsPurged?: unknown }).preheatDefaultsPurged
          ) || storedHasPreheat
        if (parsed.welds && Array.isArray(parsed.welds)) {
          const normalizedWelds: WeldEntry[] = parsed.welds.map((weld, index) => {
            const measurementLocations =
              Array.isArray(weld.measurementLocations) && weld.measurementLocations.length > 0
                ? weld.measurementLocations.map((value) => String(value))
                : [...defaultMeasurementLocations]
            const passRecordsRaw = Array.isArray(weld.passRecords)
              ? weld.passRecords.map((record) => normalizePassRecord(record, measurementLocations))
              : []
            const { weldId, weldNumber } = resolveWeldIdentityFromRecords(weld, passRecordsRaw)
            const passRecords = syncPassRecordsWeldIdentity(passRecordsRaw, weldId, weldNumber)
            return {
              id: String(weld.id || `weld-${Date.now()}-${index}`),
              weldId,
              weldNumber,
              sheetName: String(weld.sheetName || ''),
              pupOne: String(
                weld.pupOne ||
                  weld.pupNumber ||
                  (Array.isArray(weld.heatNumbers) && weld.heatNumbers.length > 0
                    ? weld.heatNumbers[0]
                    : '') ||
                  ''
              ),
              // Prefer stored pupTwo so dropdown edits persist; default Pipe 2
              // heat (heatNumbers[1]), not heatNumbers[0] which forced PUP2=PUP1.
              pupTwo: String(
                weld.pupTwo ||
                  (Array.isArray(weld.heatNumbers) && weld.heatNumbers.length > 1
                    ? weld.heatNumbers[1]
                    : Array.isArray(weld.heatNumbers) && weld.heatNumbers.length > 0
                      ? weld.heatNumbers[0]
                      : '') ||
                  ''
              ),
              pupOneSide: String((weld as StoredWeldEntry).pupOneSide ?? ''),
              pupTwoSide: String((weld as StoredWeldEntry).pupTwoSide ?? ''),
              pupOneSeamLocation: String((weld as StoredWeldEntry).pupOneSeamLocation ?? ''),
              pupTwoSeamLocation: String((weld as StoredWeldEntry).pupTwoSeamLocation ?? ''),
              heatNumbers: Array.isArray(weld.heatNumbers)
                ? weld.heatNumbers.map((value) => String(value))
                : [],
              bevelUnit: weld.bevelUnit === 'mm' ? 'mm' : 'in',
              bevelMeasurementsByPup:
                weld.bevelMeasurementsByPup && typeof weld.bevelMeasurementsByPup === 'object'
                  ? {
                      pupOne: bevelRowLabels.reduce((acc, row) => {
                        acc[row] = defaultMeasurementLocations.reduce((map, location) => {
                          const value = (weld.bevelMeasurementsByPup as any)?.pupOne?.[row]?.[location]
                          map[location] = String(value ?? '')
                          return map
                        }, {} as Record<string, string>)
                        return acc
                      }, {} as BevelMeasurementMatrix),
                      pupTwo: bevelRowLabels.reduce((acc, row) => {
                        acc[row] = defaultMeasurementLocations.reduce((map, location) => {
                          const value = (weld.bevelMeasurementsByPup as any)?.pupTwo?.[row]?.[location]
                          map[location] = String(value ?? '')
                          return map
                        }, {} as Record<string, string>)
                        return acc
                      }, {} as BevelMeasurementMatrix)
                    }
                  : {
                      pupOne: bevelRowLabels.reduce((acc, row, rowIndex) => {
                        const legacy = Array.isArray(weld.passRecords) && weld.passRecords.length > 0
                          ? normalizePassRecord(weld.passRecords[0], defaultMeasurementLocations).bevelMeasurements
                          : createPositionMap(defaultMeasurementLocations)
                        acc[row] = rowIndex === 0 ? legacy : createPositionMap(defaultMeasurementLocations)
                        return acc
                      }, {} as BevelMeasurementMatrix),
                      pupTwo: createBevelMatrix(defaultMeasurementLocations)
                    },
              measurementLocations,
              passRecords,
              operators: (() => {
                const operatorMap =
                  weld.operators?.operatorMap && typeof weld.operators?.operatorMap === 'object'
                    ? Object.keys(weld.operators?.operatorMap ?? {}).reduce((acc, key) => {
                        const entry = (weld.operators?.operatorMap as Record<string, { name?: unknown; id?: unknown }>)[
                          key
                        ]
                        acc[key] = {
                          name: String(entry?.name || ''),
                          id: String(entry?.id || '')
                        }
                        return acc
                      }, {} as Record<string, { name: string; id: string }>)
                    : {}
                const equipment = Array.isArray(weld.operators?.equipment)
                  ? weld.operators?.equipment?.map((value) => String(value)) ?? []
                  : []
                const rowsRaw = normalizeOperatorRows(
                  (weld.operators as { rows?: unknown } | undefined)?.rows,
                  operatorMap,
                  equipment
                )
                const rows = syncOperatorRowsForRootEquipment(
                  rowsRaw,
                  normalizeRootEquipment((weld as StoredWeldEntry).rootEquipment, false)
                )
                return {
                  equipment: equipmentListFromRows(rows).length > 0 ? equipmentListFromRows(rows) : equipment,
                  operatorMap: Object.keys(syncOperatorMapFromRows(rows)).length
                    ? syncOperatorMapFromRows(rows)
                    : operatorMap,
                  rows,
                  notes: String(weld.operators?.notes || '')
                }
              })(),
              limitSettings: normalizeLimitSettings(weld.limitSettings, weld.bevelUnit === 'mm' ? 'mm' : 'in'),
              slopeTime: normalizeSlopeTime((weld as StoredWeldEntry).slopeTime),
              bevelIndex: normalizeBevelIndex((weld as StoredWeldEntry).bevelIndex),
              rootEquipment: normalizeRootEquipment(
                (weld as StoredWeldEntry).rootEquipment,
                false
              ),
              preheatInterpassByPass:
                normalizePreheatInterpassByPass((weld as StoredWeldEntry).preheatInterpassByPass) ??
                {},
              preheatInterpassMeasurementsByPass: preheatDefaultsPurged
                ? normalizePreheatInterpassMeasurementsByPass(
                    (weld as StoredWeldEntry).preheatInterpassMeasurementsByPass,
                    measurementLocations
                  )
                : {},
              preheatInterpassUnit:
                (weld as StoredWeldEntry).preheatInterpassUnit === '°C' ? '°C' : '°F',
              data360ProductionWeldId:
                typeof (weld as StoredWeldEntry).data360ProductionWeldId === 'number'
                  ? (weld as StoredWeldEntry).data360ProductionWeldId
                  : undefined,
              data360ProductionId:
                typeof (weld as StoredWeldEntry).data360ProductionId === 'number'
                  ? (weld as StoredWeldEntry).data360ProductionId
                  : undefined,
              revision: normalizeRevision((weld as StoredWeldEntry).revision),
              fsExtras: normalizeFsExtras((weld as StoredWeldEntry).fsExtras),
              fsHeader: normalizeFsHeader((weld as StoredWeldEntry).fsHeader),
              fsTimes: normalizeFsTimes((weld as StoredWeldEntry).fsTimes),
              fsTimesSource:
                (weld as StoredWeldEntry).fsTimesSource === 'data360-logs' ||
                (weld as StoredWeldEntry).fsTimesSource === 'manual'
                  ? ((weld as StoredWeldEntry).fsTimesSource as 'data360-logs' | 'manual')
                  : null,
              fsTimesLogSnapshot: normalizeFsTimesLogSnapshot(
                (weld as StoredWeldEntry).fsTimesLogSnapshot
              ),
              shopFloorComments: String((weld as StoredWeldEntry).shopFloorComments ?? ''),
              deposition: normalizeDeposition(
                (weld as StoredWeldEntry).deposition,
                measurementLocations
              ),
              paramRecord: normalizeParamRecord((weld as StoredWeldEntry).paramRecord),
              fsTargets: normalizeFsTargets((weld as StoredWeldEntry).fsTargets),
              fsTargetValues: normalizeFsTargetValues((weld as StoredWeldEntry).fsTargetValues)
            }
          })
          setWelds(normalizedWelds)
          setSavedWeldsJson(JSON.stringify(normalizedWelds))
          setWeldCountDraft(String(Math.max(1, normalizedWelds.length || 1)))
        } else if (parsed.passRecords || parsed.weldNumber || parsed.operators) {
          const passRecordsRawLegacy = Array.isArray(parsed.passRecords)
            ? parsed.passRecords.map((record) => normalizePassRecord(record, defaultMeasurementLocations))
            : []
          const { weldId: wid, weldNumber: wnum } = resolveWeldIdentityFromRecords(parsed as any, passRecordsRawLegacy)
          const passRecordsLegacy = syncPassRecordsWeldIdentity(passRecordsRawLegacy, wid, wnum)
          const singleWeld: WeldEntry = {
            ...createWeldEntry(0),
            weldId: wid,
            weldNumber: wnum,
            sheetName: String(parsed.sheetName || ''),
            pupOne: '',
            pupTwo: '',
            heatNumbers: [],
            bevelUnit: 'in',
            bevelMeasurementsByPup: {
              pupOne: createBevelMatrix(defaultMeasurementLocations),
              pupTwo: createBevelMatrix(defaultMeasurementLocations)
            },
            passRecords: passRecordsLegacy,
            operators: (() => {
              const equipment = Array.isArray(parsed.operators?.equipment)
                ? parsed.operators?.equipment?.map((value: unknown) => String(value)) ?? []
                : []
              const operatorMap =
                parsed.operators?.operatorMap && typeof parsed.operators?.operatorMap === 'object'
                  ? Object.keys(parsed.operators?.operatorMap ?? {}).reduce(
                      (acc: Record<string, { name: string; id: string }>, key: string) => {
                        const entry = (
                          parsed.operators?.operatorMap as Record<
                            string,
                            { name?: unknown; id?: unknown }
                          >
                        )[key]
                        acc[key] = {
                          name: String(entry?.name || ''),
                          id: String(entry?.id || '')
                        }
                        return acc
                      },
                      {}
                    )
                  : {}
              const rows = normalizeOperatorRows(
                (parsed.operators as { rows?: unknown } | undefined)?.rows,
                operatorMap,
                equipment
              )
              return {
                equipment:
                  equipmentListFromRows(rows).length > 0
                    ? equipmentListFromRows(rows)
                    : equipment,
                operatorMap: Object.keys(syncOperatorMapFromRows(rows)).length
                  ? syncOperatorMapFromRows(rows)
                  : operatorMap,
                rows,
                notes: String(parsed.operators?.notes || '')
              }
            })(),
            limitSettings: createDefaultLimitSettings('in'),
            slopeTime: createDefaultSlopeTime()
          }
          setWelds([singleWeld])
          setSavedWeldsJson(JSON.stringify([singleWeld]))
          setWeldCountDraft('1')
        }
      } catch (error) {
        // ignore
      }
    }

  }, [pipeId, getSection, sections])

  const heatNumberMeta = useMemo(() => {
    const labels = new Map<string, string>()
    const descriptionsByHeat = new Map<string, string>()
    const pipesByIndex: Array<{ heat: string; description: string }> = []
    const values = new Set<string>()
    try {
      const baseData = getSection('base')
      if (baseData) {
        const parsed = baseData as { manufacturerLots?: unknown[] }
        const lots = Array.isArray(parsed.manufacturerLots) ? parsed.manufacturerLots : []
        lots.forEach((lot: any) => {
          if (Array.isArray(lot.pipes) && lot.pipes.length > 0) {
            lot.pipes.forEach((pipe: any, index: number) => {
              const heat = String(pipe?.heatNumber || '').trim()
              if (!heat) return
              const description =
                String(pipe?.description || '').trim() || `Pipe ${index + 1}`
              const pipeLabel = `Pipe ${index + 1}`
              // Keep first label for a heat in the option list; slot-aware
              // trigger labels use pipesByIndex so same-heat Pipe 1/2 stay distinct.
              if (!labels.has(heat)) {
                labels.set(
                  heat,
                  description && description !== pipeLabel
                    ? `${pipeLabel} - ${heat} (${description})`
                    : `${pipeLabel} - ${heat}`
                )
              }
              if (!descriptionsByHeat.has(heat)) {
                descriptionsByHeat.set(heat, description)
              }
              if (pipesByIndex.length === index) {
                pipesByIndex.push({ heat, description })
              }
              values.add(heat)
            })
            return
          }
          if (Array.isArray(lot.heatNumbers)) {
            lot.heatNumbers.forEach((value: unknown, index: number) => {
              const heat = String(value || '').trim()
              if (!heat) return
              if (!labels.has(heat)) {
                labels.set(heat, `Pipe ${index + 1} - ${heat}`)
              }
              if (pipesByIndex.length === index) {
                pipesByIndex.push({ heat, description: `Pipe ${index + 1}` })
              }
              values.add(heat)
            })
          }
        })
      }
    } catch {
      // ignore
    }
    welds.forEach((weld) => {
      ;[weld.pupOne, weld.pupTwo, ...weld.heatNumbers.map(String)].forEach((value) => {
        const trimmed = String(value || '').trim()
        if (!trimmed) return
        values.add(trimmed)
        if (!labels.has(trimmed)) {
          labels.set(trimmed, trimmed)
        }
      })
    })
    return {
      options: Array.from(values),
      labels,
      descriptionsByHeat,
      pipesByIndex
    }
  }, [getSection, welds])

  const weldIdExtras = useMemo(
    () =>
      Array.from(
        new Set(
          welds.flatMap((w) => [w.weldId, w.weldNumber]).map((s) => String(s || '').trim()).filter(Boolean)
        )
      ),
    [welds]
  )

  const weldIdSelectOptions = useMemo(() => {
    if (data360.isLinked) return data360.weldIdOptions
    return getLabelsMerged('floor_weld_id', weldIdExtras)
  }, [data360.isLinked, data360.weldIdOptions, getLabelsMerged, weldIdExtras])

  const applyWeldIdToRow = useCallback(
    async (targetRowId: string, value: string, options?: { mode?: ApplyWeldIdMode }) => {
      const decision = decideApplyWeldIdToRow({
        targetRowId,
        value,
        mode: options?.mode,
        welds,
        data360Linked: data360.isLinked,
        hasProjectAndPipe: Boolean(id && pipeId),
        findWeldByName: data360.findWeldByName
      })

      if (decision.kind === 'switch-row') {
        setActiveWeldId(decision.rowId)
        return
      }

      if (decision.kind === 'catalog-missing') {
        toast.error(`Select a weld from the ${DATA_360_DISPLAY_NAME} production list.`)
        return
      }

      if (decision.kind === 'catalog-import') {
        const status = await data360.checkImportStatus(targetRowId)
        setImportDialog({
          weldRowId: targetRowId,
          weldName: decision.match.weldName,
          productionWeldId: decision.match.productionWeldId,
          jobNumber: decision.match.jobNumber,
          replaceExisting: status.hasData
        })
        return
      }

      setWelds((prev) =>
        prev.map((w) =>
          w.id === targetRowId
            ? {
                ...w,
                weldId: decision.weldId,
                weldNumber: decision.weldId,
                passRecords: w.passRecords.map((r) => ({
                  ...r,
                  weldId: decision.weldId,
                  weldNumber: decision.weldId
                }))
              }
            : w
        )
      )
    },
    [welds, data360, id, pipeId]
  )

  const confirmData360Import = useCallback(async () => {
    if (!importDialog || !id || !pipeId) return
    const { weldRowId, weldName, productionWeldId, jobNumber } = importDialog
    setSyncingWeldRowId(weldRowId)
    try {
      const result = await data360.runWeldImport({
        weldRowId,
        productionWeldId,
        weldName,
        jobNumber
      })
      // Import writes weldparams on the server (bumps sections version). Sync
      // before any client PUT so floorsheet save cannot 409 on stale If-Match.
      syncSectionsVersion(result.sectionsVersion)
      const { sections: latestSections } = await refresh()
      const nextWelds = welds.map((w) =>
        w.id === weldRowId
          ? {
              ...w,
              weldId: weldName,
              weldNumber: weldName,
              data360ProductionWeldId: productionWeldId,
              passRecords: w.passRecords.map((r) => ({
                ...r,
                weldId: weldName,
                weldNumber: weldName
              }))
            }
          : w
      )
      setWelds(nextWelds)
      const existingFloor = latestSections.floorsheet ?? getSection('floorsheet')
      await updateSection('floorsheet', {
        ...(isFloorsheetSectionRecord(existingFloor) ? existingFloor : {}),
        preheatDefaultsPurged: true,
        welds: weldsForFloorsheetPersistence(nextWelds)
      })
      setSavedWeldsJson(JSON.stringify(nextWelds))
      toast.success(
        result.replaced
          ? `Replaced weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples, ${result.passCount} passes).`
          : `Imported weld data from ${DATA_360_DISPLAY_NAME} (${result.sampleCount} samples, ${result.passCount} passes).`
      )
    } catch (err: any) {
      toast.error(formatData360DisplayText(err?.message || `Failed to import weld from ${DATA_360_DISPLAY_NAME}.`))
    } finally {
      setSyncingWeldRowId(null)
      setImportDialog(null)
    }
  }, [importDialog, data360, refresh, syncSectionsVersion, welds, getSection, updateSection, id, pipeId])

  const passList = useMemo(() => passLabelsForBevel(null), [passLabelsForBevel])

  const preheatSectionData = useMemo(() => {
    const raw = getSection('preheat')
    return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
  }, [getSection, sections.preheat])

  const createWeldWithDetectedId = useCallback(
    (detectedWeldId: string): string => {
      const source = welds.find((w) => w.id === uploadDialogWeldId)
      const newEntry = createWeldEntry(welds.length)
      const base = {
        ...newEntry,
        weldId: detectedWeldId,
        weldNumber: detectedWeldId,
        bevelIndex: source?.bevelIndex ?? null,
        pupOne: source?.pupOne ?? '',
        pupTwo: source?.pupTwo ?? '',
        pupOneSide: source?.pupOneSide ?? '',
        pupTwoSide: source?.pupTwoSide ?? '',
        pupOneSeamLocation: source?.pupOneSeamLocation ?? '',
        pupTwoSeamLocation: source?.pupTwoSeamLocation ?? ''
      }
      const withPasses = passList.length
        ? {
            ...base,
            passRecords: ensurePassRecords(
              [],
              passList,
              base.measurementLocations,
              detectedWeldId,
              detectedWeldId
            )
          }
        : base
      setWelds((prev) => [...prev, withPasses])
      setUploadDialogWeldId(withPasses.id)
      if (withPasses.bevelIndex !== null) {
        setUploadDialogBevelIndex(withPasses.bevelIndex)
      }
      return withPasses.id
    },
    [welds, uploadDialogWeldId, passList]
  )

  const createWeldsForAlxBatch = useCallback(
    (weldIds: string[]): { entries: WeldEntry[]; nextWelds: WeldEntry[] } => {
      const source = welds.find((w) => w.id === uploadDialogWeldId)
      const entries = weldIds.map((detectedWeldId, index) => {
        const newEntry = createWeldEntry(welds.length + index)
        const base = {
          ...newEntry,
          weldId: detectedWeldId,
          weldNumber: detectedWeldId,
          bevelIndex: source?.bevelIndex ?? null,
          pupOne: source?.pupOne ?? '',
          pupTwo: source?.pupTwo ?? '',
          pupOneSide: source?.pupOneSide ?? '',
          pupTwoSide: source?.pupTwoSide ?? '',
          pupOneSeamLocation: source?.pupOneSeamLocation ?? '',
          pupTwoSeamLocation: source?.pupTwoSeamLocation ?? ''
        }
        return passList.length
          ? {
              ...base,
              passRecords: ensurePassRecords(
                [],
                passList,
                base.measurementLocations,
                detectedWeldId,
                detectedWeldId
              )
            }
          : base
      })
      return { entries, nextWelds: [...welds, ...entries] }
    },
    [welds, uploadDialogWeldId, passList]
  )

  useEffect(() => {
    setWelds((prev) => {
      if (prev.length > 0) return prev
      const created = Array.from({ length: 1 }, (_, index) => createWeldEntry(index))
      return passList.length
        ? created.map((weld) => ({
            ...weld,
            passRecords: ensurePassRecords(
              weld.passRecords,
              passList,
              weld.measurementLocations,
              weld.weldId,
              weld.weldNumber
            )
          }))
        : created
    })
  }, [passList])

  const applyWeldTargetCount = useCallback(
    (rawTarget: number) => {
      const target = Math.max(1, rawTarget)
      setWelds((prev) => {
        const current = [...prev]
        if (current.length === 0 && target > 0) {
          const created = Array.from({ length: target }, (_, index) => createWeldEntry(index))
          setWeldCountError(null)
          return passList.length
            ? created.map((weld) => ({
                ...weld,
                passRecords: ensurePassRecords(
              weld.passRecords,
              passList,
              weld.measurementLocations,
              weld.weldId,
              weld.weldNumber
            )
              }))
            : created
        }
        if (target > current.length) {
          const additions = Array.from({ length: target - current.length }, (_, index) =>
            createWeldEntry(current.length + index)
          )
          const withPasses = passList.length
            ? additions.map((weld) => ({
                ...weld,
                passRecords: ensurePassRecords(
              weld.passRecords,
              passList,
              weld.measurementLocations,
              weld.weldId,
              weld.weldNumber
            )
              }))
            : additions
          setWeldCountError(null)
          return [...current, ...withPasses]
        }
        if (target < current.length) {
          const locked = minLockedCount(current, (w) => weldEntryOccupied(w))
          if (target < locked) {
            setWeldCountError(weldTabBlockedMessage(locked))
            return current
          }
          setWeldCountError(null)
          return current.slice(0, target)
        }
        setWeldCountError(null)
        return current
      })
    },
    [passList]
  )

  useEffect(() => {
    if (weldCountError) return
    setWeldCountDraft(String(Math.max(1, welds.length || 1)))
  }, [welds.length, weldCountError])

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!weldCountError) return
      if (weldCountInputRef.current?.contains(e.target as Node)) return
      setWeldCountError(null)
      setWeldCountDraft(String(Math.max(1, welds.length || 1)))
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [weldCountError, welds.length])

  useEffect(() => {
    setWelds((prev) => {
      let changed = false
      const next = prev.map((weld) => {
        const labels = passLabelsForBevel(weld.bevelIndex)
        if (!labels.length) return weld
        const passRecords = ensurePassRecords(
          weld.passRecords,
          labels,
          weld.measurementLocations,
          weld.weldId,
          weld.weldNumber
        )
        const preheatInterpassMeasurementsByPass = ensurePreheatInterpassMeasurementsByPass(
          weld,
          labels,
          weld.measurementLocations
        )
        const preheatInterpassByPass = aggregatePreheatInterpassByPass(
          preheatInterpassMeasurementsByPass
        )
        const preheatInterpassUnit =
          weld.preheatInterpassUnit ?? defaultPreheatInterpassUnit(preheatSectionData)
        const passChanged =
          passRecords.length !== weld.passRecords.length ||
          passRecords.some((record, index) => record.passName !== weld.passRecords[index]?.passName)
        const preheatChanged =
          JSON.stringify(preheatInterpassMeasurementsByPass) !==
          JSON.stringify(weld.preheatInterpassMeasurementsByPass ?? {})
        if (
          !passChanged &&
          !preheatChanged &&
          preheatInterpassUnit === (weld.preheatInterpassUnit ?? defaultPreheatInterpassUnit(preheatSectionData))
        ) {
          return weld
        }
        changed = true
        return {
          ...weld,
          passRecords,
          preheatInterpassMeasurementsByPass,
          preheatInterpassByPass,
          preheatInterpassUnit
        }
      })
      return changed ? next : prev
    })
  }, [passLabelsForBevel, preheatSectionData, welds.length])

  useEffect(() => {
    if (!passList.length) return
    setActivePassByWeld((prev) => {
      const next = { ...prev }
      let changed = false
      welds.forEach((weld) => {
        if (!next[weld.id]) {
          next[weld.id] = 'Bevel Measurements'
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [welds, passList])

  const openSettings = (
    weld: WeldEntry,
    initialTab: 'layout' | 'targets' | 'limits' | 'slope' = 'layout'
  ) => {
    setSettingsLayoutError(null)
    setSettingsTab(initialTab)
    setSettingsWeldId(weld.id)
    settingsInitialSnapshotRef.current = [...weld.measurementLocations]
    setSettingsLocations([...weld.measurementLocations])
    setSettingsSourceIndices(weld.measurementLocations.map((_, i) => i))
    setSettingsMeasurementCountDraft(String(weld.measurementLocations.length))
    setLimitSettingsDraft(normalizeLimitSettings(weld.limitSettings, weld.bevelUnit))
    const passLabels = passLabelsForBevel(weld.bevelIndex)
    const bevelHints = bevelGeometryHintsFromResolve(
      null,
      null,
      sections as Record<string, unknown>,
      weld.bevelIndex
    )
    setTargetValuesDraft(
      mergeTargetValuesDraftWithPersisted(
        buildTargetValuesDraft(weld.deposition, passLabels, bevelHints),
        weld.fsTargetValues
      )
    )
    setTargetBevelAutoFilled({})
    const bevelSection = getSection('bevel') as { bevels?: unknown } | null | undefined
    const bevelList = Array.isArray(bevelSection?.bevels) ? bevelSection!.bevels : []
    const bevelIdx =
      weld.bevelIndex !== null && weld.bevelIndex >= 0
        ? Math.min(weld.bevelIndex, Math.max(0, bevelList.length - 1))
        : 0
    const bevelEntry = bevelList[bevelIdx] as
      | {
          jointType?: string[]
          geometryParameterProfiles?: Array<{
            jointType: string
            parameter1: string
            parameter2: string
            parameter3: string
            parameter4: string
            parameter5: string
            parameter6: string
            parameter7: string
            parameter8: string
            parameter9: string
            parameter10: string
            parameter11: string
            parameter12: string
            parameter13: string
            extraData?: string
          }>
        }
      | undefined
    const jointType = bevelEntry?.jointType?.[0]?.trim()
    if (jointType && bevelEntry?.geometryParameterProfiles) {
      const prof =
        bevelEntry.geometryParameterProfiles.find((p) => p.jointType === jointType) ??
        bevelEntry.geometryParameterProfiles[0]
      if (prof) {
        void resolveBevelGeometrySlots([
          {
            jointType,
            parameter1: prof.parameter1,
            parameter2: prof.parameter2,
            parameter3: prof.parameter3,
            parameter4: prof.parameter4,
            parameter5: prof.parameter5,
            parameter6: prof.parameter6,
            parameter7: prof.parameter7,
            parameter8: prof.parameter8,
            parameter9: prof.parameter9,
            parameter10: prof.parameter10,
            parameter11: prof.parameter11,
            parameter12: prof.parameter12,
            parameter13: prof.parameter13,
            extraData: prof.extraData ?? ''
          }
        ])
          .then(({ results }) => {
            const resolved = results[0]
            if (!resolved) return
            const hints = bevelGeometryHintsFromResolve(
              resolved.parameterLabels,
              resolved.parameterValues,
              sections as Record<string, unknown>,
              weld.bevelIndex
            )
            setTargetValuesDraft((prev) => ({
              ...prev,
              // Live bevel wins when non-blank so WPS bevel edits refresh Target Values.
              fillAngleA: hints.fillAngleA || prev.fillAngleA,
              offsetE: hints.offsetE || prev.offsetE,
              shelfHeight: hints.shelfHeight || prev.shelfHeight,
              landThickness: hints.landThickness || prev.landThickness
            }))
            setTargetBevelAutoFilled((prevFlags) => ({
              ...prevFlags,
              fillAngleA: Boolean(hints.fillAngleA),
              offsetE: Boolean(hints.offsetE),
              shelfHeight: Boolean(hints.shelfHeight),
              landThickness: Boolean(hints.landThickness)
            }))
          })
          .catch(() => {
            /* optional admin geometry — keep sync hints only */
          })
      }
    }
    const slope = normalizeSlopeTime(weld.slopeTime)
    setSlopeTimeDraft({ inSec: String(slope.inSec), outSec: String(slope.outSec) })
  }
  openSettingsRef.current = openSettings

  const updateSettingsLocation = (index: number, value: string) => {
    setSettingsLocations((prev) => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  const applySettingsMeasurementTarget = (raw: number) => {
    if (!settingsWeldId) return
    const clamped = Number.isNaN(raw) || raw < 1 ? 1 : Math.min(12, raw)
    const targetWeld = welds.find((w) => w.id === settingsWeldId)
    setSettingsLocations((prev) => {
      const { next, error } = computeResizedSettingsLocations(prev, clamped, targetWeld)
      queueMicrotask(() => setSettingsLayoutError(error))
      setSettingsSourceIndices((src) => {
        if (next.length === prev.length) return src
        if (next.length < prev.length) return src.slice(0, next.length)
        return [...src, ...Array(next.length - prev.length).fill(-1)]
      })
      return next
    })
  }

  const removeSettingsLocationRow = (removeIndex: number) => {
    if (!settingsWeldId) return
    const targetWeld = welds.find((w) => w.id === settingsWeldId)
    if (!targetWeld) return
    const locked = minLockedMeasurementColumns(targetWeld, settingsLocations)
    if (settingsLocations.length - 1 < locked) {
      setSettingsLayoutError(measurementColumnBlockedMessage(locked))
      return
    }
    setSettingsLayoutError(null)
    setSettingsLocations((prev) => prev.filter((_, i) => i !== removeIndex))
    setSettingsSourceIndices((src) => src.filter((_, i) => i !== removeIndex))
  }

  useEffect(() => {
    if (settingsLayoutError) return
    setSettingsMeasurementCountDraft(String(settingsLocations.length))
  }, [settingsLocations.length, settingsLayoutError])

  const handleSaveSettings = async () => {
    if (!settingsWeldId || savingSettings) return
    const nextWelds = welds.map((weld) => {
      if (weld.id !== settingsWeldId) return weld
      const normalizedLocations = settingsLocations.map((value, index) => {
        const trimmed = value.trim()
        return trimmed || `Location ${index + 1}`
      })
      const initialSnap = settingsInitialSnapshotRef.current
      const remapMeasurements = (measurements: Record<string, string>) =>
        normalizedLocations.reduce((acc, location, j) => {
          const srcIdx = settingsSourceIndices[j]
          if (srcIdx == null || srcIdx < 0) {
            acc[location] = ''
            return acc
          }
          const priorKey = columnKeyForLocation(initialSnap, srcIdx)
          acc[location] = measurements[priorKey] ?? ''
          return acc
        }, {} as Record<string, string>)
      const remappedPreheatMeasurements = Object.entries(
        weld.preheatInterpassMeasurementsByPass ?? {}
      ).reduce((acc, [pass, byLocation]) => {
        acc[pass] = remapMeasurements(byLocation)
        return acc
      }, {} as PreheatInterpassMeasurementsByPass)
      return {
        ...weld,
        measurementLocations: normalizedLocations,
        limitSettings: bevelRowLabels.reduce((acc, row) => {
          const draft = limitSettingsDraft[row] ?? { min: '', max: '', unit: weld.bevelUnit }
          acc[row] = {
            min: String(draft.min ?? ''),
            max: String(draft.max ?? ''),
            unit: draft.unit === 'mm' ? 'mm' : 'in'
          }
          return acc
        }, {} as WeldLimitSettings),
        slopeTime: {
          inSec: sanitizeSlopeSec(slopeTimeDraft.inSec, DEFAULT_SLOPE_TIME.inSec),
          outSec: sanitizeSlopeSec(slopeTimeDraft.outSec, DEFAULT_SLOPE_TIME.outSec)
        },
        bevelMeasurementsByPup: {
          pupOne: bevelRowLabels.reduce((acc, row) => {
            acc[row] = remapMeasurements(weld.bevelMeasurementsByPup.pupOne[row])
            return acc
          }, {} as BevelMeasurementMatrix),
          pupTwo: bevelRowLabels.reduce((acc, row) => {
            acc[row] = remapMeasurements(weld.bevelMeasurementsByPup.pupTwo[row])
            return acc
          }, {} as BevelMeasurementMatrix)
        },
        preheatInterpassMeasurementsByPass: remappedPreheatMeasurements,
        preheatInterpassByPass: aggregatePreheatInterpassByPass(remappedPreheatMeasurements),
        passRecords: weld.passRecords.map((record) => ({
          ...record,
          preheatMeasurements: remapMeasurements(record.preheatMeasurements),
          depositionMeasurements: remapMeasurements(record.depositionMeasurements),
          bevelMeasurements: remapMeasurements(record.bevelMeasurements)
        })),
        deposition: remapDepositionLocations(
          applyTargetValuesDraftToDeposition(
            weld.deposition,
            targetValuesDraft,
            normalizedLocations
          ),
          normalizedLocations
        ),
        fsTargetValues: draftToFsTargetValues(
          targetValuesDraft,
          weld.fsTargetValues.maxJointMisalignment
        )
      }
    })
    setWelds(nextWelds)
    setSettingsWeldId(null)
    if (pipeId) {
      setSavingSettings(true)
      try {
        const existing = getSection('floorsheet')
        await updateSection('floorsheet', {
          ...(isFloorsheetSectionRecord(existing) ? existing : {}),
          preheatDefaultsPurged: true,
          welds: weldsForFloorsheetPersistence(nextWelds)
        })
        setSavedWeldsJson(JSON.stringify(nextWelds))
      } catch {
        // surfaced by WpsSectionsContext
      } finally {
        setSavingSettings(false)
      }
    }
  }

  useEffect(() => {
    if (!welds.length) {
      setActiveWeldId(null)
      return
    }
    setActiveWeldId((prev) => {
      const fromUrl = searchParams.get('weldTab')
      if (fromUrl && welds.some((w) => w.id === fromUrl)) return fromUrl
      if (prev && welds.some((w) => w.id === prev)) return prev
      return welds[0].id
    })
  }, [welds, searchParams])

  const operatorRootSyncKey = useMemo(
    () =>
      welds
        .map(
          (w) =>
            `${w.id}:${w.rootEquipment}:${w.operators.rows
              .map((r) => `${r.id}|${r.equipment}|${r.name}|${r.memberId}`)
              .join(';')}`
        )
        .join('||'),
    [welds]
  )

  // Seed IWM vs Root Misfire Welder from rootEquipment; prune empty opposite type.
  useEffect(() => {
    setWelds((prev) => {
      let changed = false
      const next = prev.map((weld) => {
        const rows = syncOperatorRowsForRootEquipment(weld.operators.rows, weld.rootEquipment)
        const same =
          rows.length === weld.operators.rows.length &&
          rows.every((r, i) => {
            const o = weld.operators.rows[i]
            return (
              o &&
              r.id === o.id &&
              r.equipment === o.equipment &&
              r.direction === o.direction &&
              r.name === o.name &&
              r.memberId === o.memberId
            )
          })
        if (same) return weld
        changed = true
        return {
          ...weld,
          operators: {
            ...weld.operators,
            rows,
            equipment: equipmentListFromRows(rows),
            operatorMap: syncOperatorMapFromRows(rows)
          }
        }
      })
      return changed ? next : prev
    })
  }, [operatorRootSyncKey])

  // Sync misfire Process Details from Weld Details / clamp / target values.
  const misfirePrefillSyncKey = useMemo(
    () =>
      JSON.stringify({
        pass: sections.pass,
        weldDetails: sections.weldDetails,
        clamp: sections.clamp,
        welds: welds.map((w) => ({
          bevel: w.bevelIndex,
          wfs: w.fsTargets?.wfs ?? '',
          ops: w.operators.rows.map((r) => `${r.equipment}:${r.name}`)
        }))
      }),
    [sections.pass, sections.weldDetails, sections.clamp, welds]
  )

  useEffect(() => {
    setWelds((prev) => {
      let changed = false
      const next = prev.map((weld) => {
        const labels = passLabelsForBevel(weld.bevelIndex)
        if (!passLabelsIncludeRootMisfire(labels)) return weld
        const seed = buildMisfireMetaSeed(
          sections as Record<string, unknown>,
          weld.bevelIndex ?? 0,
          weld.operators.rows,
          weld.fsTargets?.wfs ?? ''
        )
        const misfire = mergeMisfireMetaPrefill(weld.paramRecord.misfire, seed)
        if (
          misfire.process === weld.paramRecord.misfire.process &&
          misfire.progression === weld.paramRecord.misfire.progression &&
          misfire.polarity === weld.paramRecord.misfire.polarity &&
          misfire.completedBy === weld.paramRecord.misfire.completedBy &&
          misfire.completedByMemberId === weld.paramRecord.misfire.completedByMemberId &&
          misfire.powerSupply === weld.paramRecord.misfire.powerSupply &&
          misfire.wireFeedSpeed === weld.paramRecord.misfire.wireFeedSpeed
        ) {
          return weld
        }
        changed = true
        return { ...weld, paramRecord: { ...weld.paramRecord, misfire } }
      })
      return changed ? next : prev
    })
  }, [misfirePrefillSyncKey, passLabelsForBevel, sections])

  useEffect(() => {
    if (searchParams.get('data360Rename') !== '1') return
    const weldRowId = searchParams.get('weldTab') || activeWeldId
    if (!weldRowId) return
    const next = new URLSearchParams(searchParams)
    next.delete('data360Rename')
    setSearchParams(next, { replace: true })
    if (needsData360PassMapping(getSection, weldRowId)) {
      setData360RenameDialog({ weldRowId })
    }
  }, [searchParams, activeWeldId, getSection, setSearchParams])

  useEffect(() => {
    if (!settingsLayoutError || !settingsWeldId) return
    const onPointerDown = (e: PointerEvent) => {
      if (settingsMeasurementCountRef.current?.contains(e.target as Node)) return
      setSettingsLayoutError(null)
      setSettingsMeasurementCountDraft(String(settingsLocations.length))
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [settingsLayoutError, settingsWeldId, settingsLocations.length])

  const deleteWeldAt = (weldId: string) => {
    if (welds.length <= 1) return
    setWeldCountError(null)
    setWelds((prev) => prev.filter((w) => w.id !== weldId))
    setActivePassByWeld((prev) => {
      const { [weldId]: _removed, ...rest } = prev
      return rest
    })
    setSettingsWeldId((current) => (current === weldId ? null : current))
  }

  const handleCopyApply = useCallback(
    async (nextWelds: FloorSheetCopyPanelWeld[]) => {
      const asWelds = nextWelds as WeldEntry[]
      setWelds(asWelds)
      if (!pipeId) return
      setSaving(true)
      try {
        const existing = getSection('floorsheet')
        await updateSection('floorsheet', {
          ...(isFloorsheetSectionRecord(existing) ? existing : {}),
          preheatDefaultsPurged: true,
          welds: weldsForFloorsheetPersistence(asWelds)
        })
        setSavedWeldsJson(JSON.stringify(asWelds))
        toast.success('Copied to selected welds')
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Could not copy floor sheet data')
      } finally {
        setSaving(false)
      }
    },
    [pipeId, getSection, updateSection]
  )

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    if (await persistFloorSheet()) toast.success('Floor sheet saved')
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Floor Sheet not found">
        <p className="text-sm text-heritage-grey">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  // While arriving from either the PQR Preview or the WPS Preview
  // field-link flow we swap the back-arrow to point at whichever
  // preview the user came from. `EditingReturnBanner` reads the same
  // URL to pick its copy, so the two stay in sync.
  const returnTo = returnToParam && returnToParam.trim() !== '' ? returnToParam : null
  const backPath = returnTo ?? `/projects/${project.id}/next/${pipeSet.id}`
  const backLabel = returnTo ? backToLabelFor(returnTo) : 'Back to WPS'
  const pageHeaderBackTo = isDirty
    ? { onClick: () => void proceedAfterAutoSave({ kind: 'navigate', to: backPath }), label: backLabel }
    : { to: backPath, label: backLabel }
  const pageSubtitle = formatProjectPageSubtitle({ project, pipeSet, wpsNumber })
  const swapIwmBugType = readSwapIwmBugType(getSection('meta'))

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={returnTo} />
      <PageHeader
        backTo={pageHeaderBackTo}
        title={<ProjectPageTitle label="Floor Sheet" projectName={project.name} />}
        subtitle={pageSubtitle ? <span>{pageSubtitle}</span> : null}
        actions={
          <>
            <div className="flex h-9 items-center gap-2 rounded-md border border-[#E2E5EA] bg-white px-3 shadow-sm">
              <span className="text-[11px] font-medium text-heritage-grey"># of Welds</span>
              <input
                ref={weldCountInputRef}
                className="w-14 border-0 bg-transparent text-xs font-semibold text-text-grey focus:outline-none"
                value={weldCountDraft}
                onChange={(event) => {
                  const raw = event.target.value
                  setWeldCountDraft(raw)
                  if (raw.trim() === '') return
                  applyWeldTargetCount(parseInt(raw, 10) || 0)
                }}
                onBlur={() => {
                  setWeldCountError(null)
                  setWeldCountDraft(String(Math.max(1, welds.length)))
                }}
                type="number"
                min={1}
              />
            </div>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />
      <FloorSheetIwmRootSwapBanner enabled={swapIwmBugType} />
      {weldCountError && <p className="text-xs text-error-600">{weldCountError}</p>}

      <div className="min-w-0 space-y-6 border-0 p-0">
      <div className="rounded-lg border border-[#E2E5EA] bg-white shadow-sm">
        <div className="flex flex-wrap items-stretch gap-2 border-b border-[#E2E5EA] px-4">
          {welds.map((weld, index) => {
            const isActive = weld.id === activeWeldId
            return (
              <div
                key={weld.id}
                className={`-mb-px flex min-w-0 items-stretch border-b-2 ${
                  isActive ? 'border-blueprint-blue' : 'border-transparent'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (weld.id === activeWeldId) return
                    void proceedAfterAutoSave({ kind: 'weldTab', weldId: weld.id })
                  }}
                  className={`px-4 py-2 text-sm font-semibold transition-colors ${
                    isActive ? 'text-blueprint-blue' : 'text-heritage-grey hover:text-blueprint-blue'
                  }`}
                  aria-pressed={isActive}
                >
                  Weld {index + 1}
                </button>
                {welds.length > 1 ? (
                  <button
                    type="button"
                    className="flex items-center px-2 text-heritage-grey hover:text-error-600"
                    title="Delete weld tab"
                    aria-label={`Delete weld ${index + 1}`}
                    onClick={() => deleteWeldAt(weld.id)}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            )
          })}
        </div>

        {welds
          .filter((weld) => weld.id === activeWeldId)
          .map((weld) => {
            const weldPassList = passLabelsForBevel(weld.bevelIndex)
            const isSemiAutoRoot =
              weld.rootEquipment === 'iwm_and_rc' ||
              passLabelsIncludeRootMisfire(weldPassList)
            const depositionPassList = filterDepositionPassLabels(weldPassList)
            const thermoPassList = depositionPassList
            const tabs = [
              BEVEL_MEASUREMENTS_TAB_LABEL,
              PREHEAT_INTERPASS_TAB_LABEL,
              DEPOSITION_TAB,
              ...(isSemiAutoRoot ? [ROOT_MISFIRE_TAB] : [ROOT_BEAD_TAB]),
              WELD_COMPLETION_TIMES_TAB,
              OPERATOR_INFORMATION_TAB,
              NOTES_TAB,
              MISCELLANEOUS_TAB
            ]
            const activePassRaw = activePassByWeld[weld.id] || tabs[0]
            let activePass = tabs.includes(activePassRaw) ? activePassRaw : tabs[0]
            if (isSemiAutoRoot && activePass === ROOT_BEAD_TAB) {
              activePass = ROOT_MISFIRE_TAB
            } else if (!isSemiAutoRoot && activePass === ROOT_MISFIRE_TAB) {
              activePass = ROOT_BEAD_TAB
            }
            const isBevelTab = activePass === BEVEL_MEASUREMENTS_TAB_LABEL
            const isPreheatTab = activePass === PREHEAT_INTERPASS_TAB_LABEL
            const isMiscTab = activePass === MISCELLANEOUS_TAB
            const isWeldCompletionTab = activePass === WELD_COMPLETION_TIMES_TAB
            const isOperatorTab = activePass === OPERATOR_INFORMATION_TAB
            const isNotesTab = activePass === NOTES_TAB
            const isCrcExtraTab =
              activePass === DEPOSITION_TAB ||
              activePass === ROOT_BEAD_TAB ||
              activePass === ROOT_MISFIRE_TAB
            const miscDraft = buildMiscellaneousDraft(
              weld.deposition,
              depositionPassList,
              weld.fsTargetValues.maxJointMisalignment ||
                readBevelMaxJointMisalignment(
                  sections as Record<string, unknown>,
                  weld.bevelIndex
                )
            )
            const miscMisalignmentAutoFilled = Boolean(
              !weld.fsTargetValues.maxJointMisalignment.trim() &&
                readBevelMaxJointMisalignment(
                  sections as Record<string, unknown>,
                  weld.bevelIndex
                )
            )
            const preheatAccessible = canAccessPreheatTab(weld)
            const weldIdPreflight = evaluateFloorSheetWeldIdPreflight({
              weldId: weld.weldId || weld.weldNumber,
              pupOne: weld.pupOne,
              pupTwo: weld.pupTwo,
              bevelIndex: weld.bevelIndex
            })
            const weldIdLocked = weldIdPreflight.kind !== 'allowed'
            return (
              <div key={weld.id} className="p-4">
                <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-page-bg p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-heritage-grey">Pass Records</p>
                        <button
                          type="button"
                          className="rounded-md border border-[#E2E5EA] bg-white p-1.5 text-heritage-grey shadow-sm transition-colors hover:bg-[#FAFBFC] hover:text-blueprint-blue"
                          aria-label="Pass record settings"
                          onClick={() => openSettings(weld)}
                        >
                          <Cog6ToothIcon className="h-4 w-4" />
                        </button>
                        <Button
                          variant="secondary"
                          className="h-7 px-2 text-[11px]"
                          data-testid="floorsheet-parameters-button"
                          onClick={async () => {
                            const outcome = evaluateFloorSheetParametersPreflight({
                              hasPersistedParams: Boolean(
                                id && pipeId && hasPersistedWeldParams(getSection, weld.id)
                              ),
                              weldId: weld.weldId,
                              pupOne: weld.pupOne,
                              pupTwo: weld.pupTwo,
                              bevelIndex: weld.bevelIndex
                            })

                            switch (outcome.kind) {
                              case 'navigate':
                                if (
                                  hasPersistedWeldParams(getSection, weld.id) &&
                                  needsData360PassMapping(getSection, weld.id)
                                ) {
                                  setData360RenameDialog({ weldRowId: weld.id })
                                  return
                                }
                                void proceedAfterAutoSave({
                                  kind: 'navigate',
                                  to: `/projects/${id}/next/${pipeId}/floorsheet/${weld.id}/parameters`
                                })
                                return
                              case 'missing-pup':
                                applyFieldPreflightError(weld.id, outcome, 'parameters')
                                return
                              case 'missing-bevel':
                                applyFieldPreflightError(weld.id, outcome, 'parameters')
                                return
                              case 'open-unified-upload-dialog':
                                setUploadDialogBevelIndex(outcome.bevelIndex)
                                setUploadDialogWeldId(weld.id)
                                return
                            }
                          }}
                        >
                          Parameters
                        </Button>
                      </div>
                      <p className="mt-1 text-[11px] text-heritage-grey">
                        Record measurements for each pass at the specified positions.
                      </p>
                    </div>
                    <div className="flex w-full flex-row flex-wrap items-start gap-3 sm:w-auto sm:justify-end">
                      <div className="w-full min-w-[12rem] sm:w-auto sm:max-w-[14rem]">
                        <label className="mb-1 block text-[11px] font-medium text-heritage-grey">
                          Bevel
                        </label>
                        <MultiSelect
                          className="w-full"
                          maxSelections={1}
                          values={weld.bevelIndex !== null ? [String(weld.bevelIndex)] : []}
                          options={wpsBevelOptions.map((b) => String(b.index))}
                          getOptionLabel={(v) =>
                            wpsBevelOptions.find((b) => String(b.index) === v)?.label ?? v
                          }
                          onChange={(values) => {
                            const nextIndex =
                              values.length > 0 && Number.isFinite(Number(values[0]))
                                ? Number(values[0])
                                : null
                            if (nextIndex === null) {
                              setWelds((prev) =>
                                prev.map((item) =>
                                  item.id === weld.id
                                    ? { ...item, bevelIndex: null, rootEquipment: 'iwm' }
                                    : item
                                )
                              )
                              return
                            }
                            const nextLabels = passLabelsForBevel(nextIndex)
                            setWelds((prev) =>
                              prev.map((item) => {
                                if (item.id !== weld.id) return item
                                const passRecords = nextLabels.length
                                  ? ensurePassRecords(
                                      item.passRecords,
                                      nextLabels,
                                      item.measurementLocations,
                                      item.weldId,
                                      item.weldNumber
                                    )
                                  : item.passRecords
                                const preheatInterpassMeasurementsByPass = nextLabels.length
                                  ? ensurePreheatInterpassMeasurementsByPass(
                                      {
                                        preheatInterpassMeasurementsByPass:
                                          item.preheatInterpassMeasurementsByPass
                                      },
                                      nextLabels,
                                      item.measurementLocations
                                    )
                                  : item.preheatInterpassMeasurementsByPass
                                const preheatInterpassByPass = nextLabels.length
                                  ? aggregatePreheatInterpassByPass(
                                      preheatInterpassMeasurementsByPass ?? {}
                                    )
                                  : item.preheatInterpassByPass
                                return {
                                  ...item,
                                  bevelIndex: nextIndex,
                                  passRecords,
                                  preheatInterpassMeasurementsByPass,
                                  preheatInterpassByPass
                                }
                              })
                            )
                            if (values.length > 0 && paramErrorByWeld[weld.id]?.field === 'bevel') {
                              setParamErrorByWeld((prev) => ({ ...prev, [weld.id]: null }))
                            }
                          }}
                          placeholder="Select bevel"
                          searchPlaceholder="Search bevels"
                          interactionLocked={hasPersistedWeldParams(getSection, weld.id)}
                          lockedHint="Bevel selection cannot be changed after weld data has been imported."
                        />
                        {paramErrorByWeld[weld.id]?.field === 'bevel' ? (
                          <p
                            className="mt-1 text-[11px] text-error-600"
                            data-testid="floorsheet-bevel-error"
                          >
                            {paramErrorByWeld[weld.id]?.message}
                          </p>
                        ) : null}
                        {weld.bevelIndex !== null ? (
                          <FloorSheetRootEquipmentRadios
                            value={weld.rootEquipment}
                            onChange={(pending) => {
                              if (pending === weld.rootEquipment) return
                              setRootEquipmentChangeDialog({ weldId: weld.id, pending })
                            }}
                          />
                        ) : null}
                      </div>
                      <div className="w-full min-w-[12rem] sm:w-auto sm:max-w-[14rem]">
                        <label className="mb-1 block text-[11px] font-medium text-heritage-grey">
                          Weld ID
                        </label>
                        <WeldIdCatalogSelect
                          selectRef={weldIdSelectRef}
                          value={weld.weldId || weld.weldNumber}
                          options={weldIdSelectOptions}
                          interactionLocked={weldIdLocked}
                          lockedHint="Select Pup 1, Pup 2, and Bevel before choosing a Weld ID."
                          onLockedInteraction={() => {
                            if (weldIdPreflight.kind === 'allowed') return
                            applyFieldPreflightError(weld.id, weldIdPreflight, 'weldId')
                          }}
                          onChange={(value) => {
                            void applyWeldIdToRow(weld.id, value)
                            if (value.trim() && paramErrorByWeld[weld.id]?.field === 'weldId') {
                              setParamErrorByWeld((prev) => ({ ...prev, [weld.id]: null }))
                            }
                          }}
                          onCreateOption={
                            data360.isLinked
                              ? async (bare) => bare.trim()
                              : async (bare) => {
                                  if (!project?.id) return ''
                                  const trimmed = bare.trim()
                                  if (!trimmed) return ''
                                  await addLabel('floor_weld_id', trimmed, project.id)
                                  await applyWeldIdToRow(weld.id, trimmed)
                                  return trimmed
                                }
                          }
                          errorMessage={
                            paramErrorByWeld[weld.id]?.field === 'weldId'
                              ? paramErrorByWeld[weld.id]?.message ?? null
                              : null
                          }
                        />
                        {data360.isLinked && data360.weldLoadError ? (
                          <p className="mt-1 text-[11px] text-error-600">{formatData360DisplayText(data360.weldLoadError)}</p>
                        ) : null}
                        {syncingWeldRowId === weld.id ? (
                          <p className="mt-1 text-[11px] text-heritage-grey">Importing from {DATA_360_DISPLAY_NAME}…</p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 border-b border-[#E2E5EA]">
                    <FloorSheetPassRecordTabStrip
                      tabs={tabs}
                      activeTab={activePass}
                      weldId={weld.id}
                      weldFocusId={weld.weldId || weld.id}
                      isTabDisabled={(pass) =>
                        pass === PREHEAT_INTERPASS_TAB_LABEL && !preheatAccessible
                      }
                      tabTitle={(pass) =>
                        pass === PREHEAT_INTERPASS_TAB_LABEL && !preheatAccessible
                          ? 'Select Bevel, Pup 1, and Pup 2 before entering preheat / interpass limits.'
                          : undefined
                      }
                      onTabClick={(pass) => {
                        const isPreheat = pass === PREHEAT_INTERPASS_TAB_LABEL
                        const blocked = isPreheat && !preheatAccessible
                        if (blocked) {
                          if (!weld.pupOne.trim()) {
                            applyFieldPreflightError(
                              weld.id,
                              { kind: 'missing-pup', pup: 'pupOne' },
                              'parameters'
                            )
                            return
                          }
                          if (!weld.pupTwo.trim()) {
                            applyFieldPreflightError(
                              weld.id,
                              { kind: 'missing-pup', pup: 'pupTwo' },
                              'parameters'
                            )
                            return
                          }
                          if (weld.bevelIndex === null) {
                            applyFieldPreflightError(
                              weld.id,
                              { kind: 'missing-bevel' },
                              'parameters'
                            )
                            return
                          }
                          return
                        }
                        setActivePassByWeld((prev) => ({
                          ...prev,
                          [weld.id]: pass
                        }))
                      }}
                    />
                    <FloorSheetCopyPanel
                      activeTab={activePass}
                      sourceWeld={weld as FloorSheetCopyPanelWeld}
                      sourceWeldIndex={welds.findIndex((item) => item.id === weld.id)}
                      sourcePassLabels={depositionPassList}
                      welds={welds as FloorSheetCopyPanelWeld[]}
                      busy={saving}
                      onApply={handleCopyApply}
                    />
                  </div>

                  {isBevelTab ? (
                        <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-white">
                          <div className="flex items-center justify-between border-b border-[#E2E5EA] bg-page-bg px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-heritage-grey">
                            <span>Bevel Measurements ({weld.bevelUnit})</span>
                            <InlineSelect
                              value={weld.bevelUnit}
                              options={[
                                { value: 'in', label: 'in' },
                                { value: 'mm', label: 'mm' }
                              ]}
                              onChange={(value) =>
                                setWelds((prev) =>
                                  prev.map((item) =>
                                    item.id === weld.id ? { ...item, bevelUnit: value as 'in' | 'mm' } : item
                                  )
                                )
                              }
                              className="w-16"
                              buttonClassName="px-2 py-1 text-xs justify-between"
                            />
                          </div>

                          <div className="overflow-x-auto">
                            <table className="w-full text-sm text-text-grey">
                              <thead className="bg-page-bg text-[11px] font-bold uppercase tracking-wider text-heritage-grey">
                                <tr className="border-b border-[#E2E5EA]">
                                  <th className="px-3 py-2.5 text-left whitespace-nowrap">Measurement</th>
                                  {weld.measurementLocations.map((location, locationIndex) => (
                                    <th key={`${weld.id}-${location}-${locationIndex}-bevel-col`} className="px-3 py-2.5 text-left whitespace-nowrap">
                                      {location}
                                    </th>
                                  ))}
                                  <th className="px-3 py-2.5 text-left whitespace-nowrap">Pup #</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(['pupOne', 'pupTwo'] as const).map((pupKey, pupIndex) =>
                                  bevelRowLabels.map((row, rowIndex) => (
                                    <tr key={`${weld.id}-${pupKey}-${row}`} className="border-b border-[#EEF0F3] hover:bg-[#FAFBFC] transition-colors">
                                      <td className="px-3 py-2.5 font-medium text-text-grey whitespace-nowrap">{row}</td>
                                      {weld.measurementLocations.map((location, locationIndex) => (
                                        <td key={`${weld.id}-${pupKey}-${row}-${location}-${locationIndex}`} className="px-3 py-2 tabular-nums">
                                          <input
                                            className={`w-full rounded-md border px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 ${limitInputClassName(
                                              weld.bevelMeasurementsByPup[pupKey][row][location],
                                              weld.limitSettings?.[row] ?? { min: '', max: '', unit: weld.bevelUnit }
                                            )}`}
                                            value={weld.bevelMeasurementsByPup[pupKey][row][location]}
                                            onChange={(event) => {
                                              const value = event.target.value
                                              setWelds((prev) =>
                                                prev.map((item) => {
                                                  if (item.id !== weld.id) return item
                                                  return {
                                                    ...item,
                                                    bevelMeasurementsByPup: {
                                                      ...item.bevelMeasurementsByPup,
                                                      [pupKey]: {
                                                        ...item.bevelMeasurementsByPup[pupKey],
                                                        [row]: {
                                                          ...item.bevelMeasurementsByPup[pupKey][row],
                                                          [location]: value
                                                        }
                                                      }
                                                    }
                                                  }
                                                })
                                              )
                                            }}
                                            type="number"
                                            min={0}
                                            placeholder="--"
                                          />
                                        </td>
                                      ))}
                                      {rowIndex === 0 ? (
                                        <td className="px-3 py-2 align-top" rowSpan={bevelRowLabels.length}>
                                          <div className="space-y-2">
                                            <div className="text-[11px] font-bold uppercase tracking-wider text-heritage-grey">
                                              Pup {pupIndex + 1}
                                            </div>
                                            <SingleSelect
                                              ref={pupKey === 'pupOne' ? pupOneSelectRef : pupTwoSelectRef}
                                              value={pupKey === 'pupOne' ? weld.pupOne : weld.pupTwo}
                                              options={heatNumberMeta.options}
                                              getOptionLabel={(option) => heatNumberMeta.labels.get(option) || option}
                                              getTriggerLabel={(option) => {
                                                const slot = heatNumberMeta.pipesByIndex[pupIndex]
                                                const desc =
                                                  slot && slot.heat === option
                                                    ? slot.description || `Pipe ${pupIndex + 1}`
                                                    : heatNumberMeta.descriptionsByHeat.get(option)
                                                return formatPupTriggerLabel(option, desc)
                                              }}
                                              onChange={(value) => {
                                                setWelds((prev) =>
                                                  prev.map((item) =>
                                                    item.id === weld.id
                                                      ? {
                                                          ...item,
                                                          [pupKey]: value
                                                        }
                                                      : item
                                                  )
                                                )
                                                if (value.trim() && paramErrorByWeld[weld.id]?.field === pupKey) {
                                                  setParamErrorByWeld((prev) => ({ ...prev, [weld.id]: null }))
                                                }
                                              }}
                                              placeholder="Select heat number"
                                              inputClassName="h-8 text-xs"
                                              searchPlaceholder="Search by heat number or description"
                                            />
                                            {paramErrorByWeld[weld.id]?.field === pupKey ? (
                                              <p
                                                className="text-[11px] text-error-600"
                                                data-testid={`floorsheet-${pupKey}-error`}
                                              >
                                                {paramErrorByWeld[weld.id]?.message}
                                              </p>
                                            ) : null}
                                            <div>
                                              <label className="mt-2 block text-[10px] font-medium text-heritage-grey">
                                                Side
                                              </label>
                                              <input
                                                type="text"
                                                className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-xs focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                value={pupKey === 'pupOne' ? weld.pupOneSide : weld.pupTwoSide}
                                                onChange={(event) => {
                                                  const value = event.target.value
                                                  const sideKey = pupKey === 'pupOne' ? 'pupOneSide' : 'pupTwoSide'
                                                  setWelds((prev) =>
                                                    prev.map((item) =>
                                                      item.id === weld.id ? { ...item, [sideKey]: value } : item
                                                    )
                                                  )
                                                }}
                                                placeholder="—"
                                              />
                                            </div>
                                            <div>
                                              <label className="mt-2 block text-[10px] font-medium text-heritage-grey">
                                                Seam location
                                              </label>
                                              <SingleSelect
                                                value={pupKey === 'pupOne' ? weld.pupOneSeamLocation : weld.pupTwoSeamLocation}
                                                options={clockLocationOptions}
                                                onChange={(value) => {
                                                  const seamKey =
                                                    pupKey === 'pupOne' ? 'pupOneSeamLocation' : 'pupTwoSeamLocation'
                                                  setWelds((prev) =>
                                                    prev.map((item) =>
                                                      item.id === weld.id ? { ...item, [seamKey]: value } : item
                                                    )
                                                  )
                                                }}
                                                placeholder="Select seam location"
                                                inputClassName="h-8 text-xs"
                                                searchPlaceholder="Search seam location"
                                              />
                                            </div>
                                          </div>
                                        </td>
                                      ) : null}
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div className="border-t border-[#E2E5EA] bg-page-bg px-3 py-3">
                            <div className="text-[11px] font-bold uppercase tracking-wider text-heritage-grey">Stats</div>
                            <div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                              {bevelRowLabels.map((row) => {
                                const stats = getBevelStats(weld.bevelMeasurementsByPup, bevelRowLabels, weld.measurementLocations)[row]
                                const rowRule = weld.limitSettings?.[row] ?? { min: '', max: '', unit: weld.bevelUnit }
                                return (
                                  <div
                                    key={`${weld.id}-stats-${row}`}
                                    className="rounded-md border border-[#E2E5EA] bg-white px-3 py-2"
                                  >
                                    <div className="text-[11px] text-heritage-grey">{row}</div>
                                    <div className="mt-1 text-sm text-text-grey tabular-nums">
                                      Min:{' '}
                                      <span className={limitStatValueClass(stats.min, rowRule)}>
                                        {stats.min !== null ? stats.min : '--'}
                                      </span>
                                      {' '}
                                      • Avg:{' '}
                                      <span className={limitStatValueClass(stats.avg, rowRule)}>
                                        {stats.avg !== null ? stats.avg.toFixed(2) : '--'}
                                      </span>
                                      {' '}
                                      • Max:{' '}
                                      <span className={limitStatValueClass(stats.max, rowRule)}>
                                        {stats.max !== null ? stats.max : '--'}
                                      </span>
                                    </div>
                                    {(rowRule.min.trim() || rowRule.max.trim()) && (
                                      <div className="mt-1 text-[10px] text-heritage-grey">
                                        In range (no highlight): {rowRule.min.trim() || '—'} to {rowRule.max.trim() || '—'}{' '}
                                        {rowRule.unit}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      ) : isPreheatTab ? (
                        <div className="mt-4 rounded-lg border border-[#E2E5EA] bg-white">
                          <div className="flex items-center justify-between border-b border-[#E2E5EA] bg-page-bg px-3 py-2.5 text-[11px] font-bold uppercase tracking-wider text-heritage-grey">
                            <span>Preheat / Interpass temperatures</span>
                            <InlineSelect
                              value={weld.preheatInterpassUnit ?? defaultPreheatInterpassUnit(preheatSectionData)}
                              options={[
                                { value: '°F', label: '°F' },
                                { value: '°C', label: '°C' }
                              ]}
                              onChange={(value) =>
                                setWelds((prev) =>
                                  prev.map((item) =>
                                    item.id === weld.id
                                      ? { ...item, preheatInterpassUnit: value as '°F' | '°C' }
                                      : item
                                  )
                                )
                              }
                              className="w-16"
                              buttonClassName="px-2 py-1 text-xs justify-between"
                            />
                          </div>
                          {!preheatAccessible ? (
                            <p className="px-4 py-6 text-sm text-heritage-grey">
                              Select Bevel, Pup 1, and Pup 2 before recording preheat /
                              interpass temperatures.
                            </p>
                          ) : thermoPassList.length === 0 ? (
                            <p className="px-4 py-6 text-sm text-heritage-grey">
                              Select a bevel to record preheat and interpass temperatures per pass.
                            </p>
                          ) : (
                            <div className="overflow-x-auto px-4 py-3">
                              <table className="w-full text-sm text-text-grey">
                                <thead className="bg-page-bg text-[11px] font-bold uppercase tracking-wider text-heritage-grey">
                                  <tr className="border-b border-[#E2E5EA]">
                                    <th className="px-3 py-2 text-left whitespace-nowrap">
                                      Pass
                                    </th>
                                    {weld.measurementLocations.map((location, locationIndex) => (
                                      <th
                                        key={`${weld.id}-preheat-loc-${location}-${locationIndex}`}
                                        className="border-l border-[#E2E5EA] px-3 py-2 text-center whitespace-nowrap"
                                      >
                                        {location}
                                      </th>
                                    ))}
                                    <th className="border-l border-[#E2E5EA] bg-[rgba(230,161,0,0.12)] px-3 py-2 text-center whitespace-nowrap text-[#8A6A00]">
                                      Min
                                    </th>
                                    <th className="border-l border-[#E2E5EA] bg-[rgba(230,161,0,0.12)] px-3 py-2 text-center whitespace-nowrap text-[#8A6A00]">
                                      Max
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {thermoPassList.map((passName) => {
                                    const byLocation =
                                      weld.preheatInterpassMeasurementsByPass?.[passName] ?? {}
                                    const bounds = weld.preheatInterpassByPass?.[passName]
                                    const minDisplay = formatPreheatBoundDisplay(bounds?.min)
                                    const maxDisplay = formatPreheatBoundDisplay(bounds?.max)
                                    return (
                                      <tr
                                        key={`${weld.id}-preheat-${passName}`}
                                        className="border-b border-[#EEF0F3] transition-colors hover:bg-[#FAFBFC]"
                                      >
                                        <td className="px-3 py-2 font-medium text-text-grey whitespace-nowrap">
                                          {passName}
                                        </td>
                                        {weld.measurementLocations.map((location, locationIndex) => {
                                          const value = byLocation[location] ?? ''
                                          const updateCell = (next: string) => {
                                            setWelds((prev) =>
                                              prev.map((item) => {
                                                if (item.id !== weld.id) return item
                                                const currentMeasurements =
                                                  item.preheatInterpassMeasurementsByPass ?? {}
                                                const currentPass =
                                                  currentMeasurements[passName] ?? {}
                                                const nextMeasurements = {
                                                  ...currentMeasurements,
                                                  [passName]: {
                                                    ...currentPass,
                                                    [location]: next
                                                  }
                                                }
                                                return {
                                                  ...item,
                                                  preheatInterpassMeasurementsByPass:
                                                    nextMeasurements,
                                                  preheatInterpassByPass:
                                                    aggregatePreheatInterpassByPass(nextMeasurements)
                                                }
                                              })
                                            )
                                          }
                                          return (
                                            <td
                                              key={`${weld.id}-preheat-${passName}-${location}-${locationIndex}`}
                                              className="border-l border-[#EEF0F3] px-2 py-1.5 text-center tabular-nums"
                                            >
                                              <input
                                                className="mx-auto block w-16 rounded-md border border-gray-300 px-2 py-1 text-center text-xs text-text-grey focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                value={value}
                                                onChange={(event) =>
                                                  updateCell(event.target.value)
                                                }
                                                type="number"
                                                placeholder="--"
                                              />
                                            </td>
                                          )
                                        })}
                                        <td
                                          className="border-l border-[#EEF0F3] bg-[rgba(230,161,0,0.06)] px-3 py-2 text-center tabular-nums text-xs font-semibold text-text-grey"
                                          data-testid={`preheat-min-${passName}`}
                                        >
                                          {minDisplay}
                                        </td>
                                        <td
                                          className="border-l border-[#EEF0F3] bg-[rgba(230,161,0,0.06)] px-3 py-2 text-center tabular-nums text-xs font-semibold text-text-grey"
                                          data-testid={`preheat-max-${passName}`}
                                        >
                                          {maxDisplay}
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      ) : isWeldCompletionTab ? (
                        <FloorSheetWeldCompletionTimes
                          focusWeldId={weld.weldId || weld.id}
                          fsTimes={weld.fsTimes}
                          logSnapshot={weld.fsTimesLogSnapshot}
                          importFromLogsBusy={fsTimesImportBusyWeldId === weld.id}
                          importFromLogsDisabled={!id || !pipeId}
                          onImportFromLogs={() => importFsTimesFromData360(weld.id)}
                          onTimesChange={(fsTimes) =>
                            setWelds((prev) =>
                              prev.map((item) =>
                                item.id === weld.id
                                  ? { ...item, fsTimes, fsTimesSource: 'manual' }
                                  : item
                              )
                            )
                          }
                        />
                      ) : isOperatorTab ? (
                        <div>
                          <div className="mt-2 flex justify-end">
                            {id ? (
                              <Link
                                to={`/projects/${id}/team-members?returnTo=${encodeURIComponent(
                                  location.pathname + location.search
                                )}`}
                                className="text-xs font-medium text-blueprint-blue hover:underline"
                              >
                                Manage team members
                              </Link>
                            ) : null}
                          </div>
                          <FloorSheetOperatorPanel
                            weldId={weld.id}
                            focusWeldId={weld.weldId || weld.id}
                            projectId={id}
                            project={project}
                            projectEquipment={projectEquipmentList}
                            rows={weld.operators.rows}
                            teamMembers={teamMembersForOperators}
                            teamMembersLoading={teamMembersLoading}
                            memberById={memberById}
                            onRefreshTeamMembers={refreshTeamMembersForOperators}
                            onChangeRows={(rows) =>
                              setWelds((prev) =>
                                prev.map((item) =>
                                  item.id === weld.id
                                    ? {
                                        ...item,
                                        operators: {
                                          ...item.operators,
                                          rows,
                                          equipment: equipmentListFromRows(rows),
                                          operatorMap: syncOperatorMapFromRows(rows)
                                        }
                                      }
                                    : item
                                )
                              )
                            }
                          />
                        </div>
                      ) : isNotesTab ? (
                        <div
                          className="mt-4 rounded-lg border border-[#E2E5EA] bg-white p-4"
                          data-testid="floorsheet-notes"
                          data-focus-field="operators.notes"
                          data-focus-weld-id={weld.weldId || weld.id}
                        >
                          <label className="text-xs font-medium text-heritage-grey">Notes</label>
                          <textarea
                            className="input-primary mt-1 h-24"
                            value={weld.operators.notes}
                            onChange={(event) =>
                              setWelds((prev) =>
                                prev.map((item) =>
                                  item.id === weld.id
                                    ? {
                                        ...item,
                                        operators: {
                                          ...item.operators,
                                          notes: event.target.value
                                        }
                                      }
                                    : item
                                )
                              )
                            }
                            placeholder="Add notes or crew details..."
                          />
                        </div>
                      ) : isMiscTab ? (
                        <FloorSheetMiscellaneousPanel
                          draft={miscDraft}
                          misalignmentAutoFilled={miscMisalignmentAutoFilled}
                          onChange={(next) => {
                            const applied = applyMiscellaneousDraftToWeldFields(
                              weld.deposition,
                              next,
                              weld.measurementLocations,
                              weld.fsTargetValues
                            )
                            setWelds((prev) =>
                              prev.map((item) =>
                                item.id === weld.id
                                  ? {
                                      ...item,
                                      deposition: applied.deposition,
                                      fsTargetValues: applied.fsTargetValues
                                    }
                                  : item
                              )
                            )
                          }}
                        />
                      ) : isCrcExtraTab ? (
                        <FloorSheetCrcTabBody
                          weldId={weld.id}
                          focusWeldId={weld.weldId || weld.id}
                          locations={weld.measurementLocations}
                          passLabels={depositionPassList}
                          deposition={weld.deposition}
                          paramRecord={weld.paramRecord}
                          activeTab={activePass}
                          onDepositionChange={(deposition) =>
                            setWelds((prev) =>
                              prev.map((item) => (item.id === weld.id ? { ...item, deposition } : item))
                            )
                          }
                          onParamRecordChange={(update) =>
                            setWelds((prev) =>
                              prev.map((item) =>
                                item.id === weld.id
                                  ? { ...item, paramRecord: update(item.paramRecord) }
                                  : item
                              )
                            )
                          }
                          iwmTravelSpeed={weld.fsTargets?.travelSpeed ?? ''}
                          onIwmTravelSpeedChange={(travelSpeed) =>
                            setWelds((prev) =>
                              prev.map((item) =>
                                item.id === weld.id
                                  ? {
                                      ...item,
                                      fsTargets: { ...item.fsTargets, travelSpeed }
                                    }
                                  : item
                              )
                            )
                          }
                          projectId={id}
                          teamMembers={teamMembersForOperators}
                          teamMembersLoading={teamMembersLoading}
                          memberById={memberById}
                          misfireMetaSeed={buildMisfireMetaSeed(
                            sections as Record<string, unknown>,
                            weld.bevelIndex ?? 0,
                            weld.operators.rows,
                            weld.fsTargets?.wfs ?? ''
                          )}
                          powerSupplyOptionExtras={misfirePowerSupplyOptions}
                        />
                      ) : null}
                </div>
              </div>
            )
          })}
      </div>
      </div>

      {settingsWeldId ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4">
          <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-text-grey">Measurement Settings</p>
                <p className="mt-1 text-xs text-heritage-grey">
                  Set how many measurement points and name each location.
                </p>
              </div>
              <button
                type="button"
                className="rounded-md p-1 text-heritage-grey transition-colors hover:bg-page-bg hover:text-blueprint-blue"
                onClick={() => setSettingsWeldId(null)}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="mt-4">
              <div className="flex items-center gap-2 border-b border-[#E2E5EA]">
                <button
                  type="button"
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    settingsTab === 'layout'
                      ? 'border-blueprint-blue text-blueprint-blue'
                      : 'border-transparent text-heritage-grey hover:text-blueprint-blue'
                  }`}
                  onClick={() => setSettingsTab('layout')}
                >
                  Existing Settings
                </button>
                <button
                  type="button"
                  data-testid="settings-tab-targets"
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    settingsTab === 'targets'
                      ? 'border-blueprint-blue text-blueprint-blue'
                      : 'border-transparent text-heritage-grey hover:text-blueprint-blue'
                  }`}
                  onClick={() => setSettingsTab('targets')}
                >
                  Target Values
                </button>
                <button
                  type="button"
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    settingsTab === 'limits'
                      ? 'border-blueprint-blue text-blueprint-blue'
                      : 'border-transparent text-heritage-grey hover:text-blueprint-blue'
                  }`}
                  onClick={() => setSettingsTab('limits')}
                >
                  Limit Settings
                </button>
                <button
                  type="button"
                  data-testid="settings-tab-slope"
                  className={`-mb-px border-b-2 px-3 py-2 text-sm font-semibold transition-colors ${
                    settingsTab === 'slope'
                      ? 'border-blueprint-blue text-blueprint-blue'
                      : 'border-transparent text-heritage-grey hover:text-blueprint-blue'
                  }`}
                  onClick={() => setSettingsTab('slope')}
                >
                  Slope Time (sec)
                </button>
              </div>

              <div className="mt-4 h-[28rem] overflow-y-auto">
              {settingsTab === 'layout' && (
                <>
                  <div>
                    <label className="text-xs font-medium text-heritage-grey">Number of measurements</label>
                    <input
                      ref={settingsMeasurementCountRef}
                      className="input-primary mt-1 w-24"
                      type="number"
                      min={1}
                      max={12}
                      value={settingsMeasurementCountDraft}
                      onChange={(event) => {
                        const raw = event.target.value
                        setSettingsMeasurementCountDraft(raw)
                        if (raw.trim() === '') return
                        applySettingsMeasurementTarget(parseInt(raw, 10) || 0)
                      }}
                      onBlur={() => {
                        setSettingsLayoutError(null)
                        setSettingsMeasurementCountDraft(String(settingsLocations.length))
                      }}
                    />
                    {settingsLayoutError && (
                      <p className="mt-2 text-xs text-error-600">{settingsLayoutError}</p>
                    )}
                  </div>

                  <div className="mt-4">
                    <label className="text-xs font-medium text-heritage-grey">Locations</label>
                    <div className="mt-2 space-y-2">
                      {settingsLocations.map((location, index) => {
                        const usedLocations = settingsLocations.filter(
                          (value, valueIndex) => value && valueIndex !== index
                        )
                        const availableLocations = clockLocationOptions.filter(
                          (option) => !usedLocations.includes(option)
                        )
                        return (
                        <div key={`location-${index}`} className="flex items-center gap-3">
                          <div className="w-8 text-xs text-heritage-grey">#{index + 1}</div>
                          <div className="min-w-0 flex-1">
                            <Combobox
                              value={location}
                              options={availableLocations}
                              onChange={(value) => updateSettingsLocation(index, value)}
                              placeholder="Select location"
                              allowCreate={false}
                            />
                          </div>
                          <button
                            type="button"
                            className={`inline-flex shrink-0 items-center rounded-md p-2 text-error-600 transition hover:bg-error-50 ${
                              settingsLocations.length <= 1 ? 'cursor-not-allowed opacity-40' : ''
                            }`}
                            disabled={settingsLocations.length <= 1}
                            title={
                              settingsLocations.length <= 1
                                ? 'At least one measurement location is required.'
                                : 'Remove this location'
                            }
                            aria-label={`Remove measurement location ${index + 1}`}
                            onClick={() => removeSettingsLocationRow(index)}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                        )
                      })}
                    </div>
                  </div>
                </>
              )}

              {settingsTab === 'targets' && (
                <div
                  data-focus-field="settings.targets"
                  data-focus-weld-id={
                    welds.find((w) => w.id === settingsWeldId)?.weldId ||
                    settingsWeldId ||
                    undefined
                  }
                >
                  <p className="text-xs text-heritage-grey">
                    One target value per field. Bevel geometry and deposition data auto-fill when
                    available; fill in anything that is still blank.
                  </p>
                  <FloorSheetTargetValuesPanel
                    draft={targetValuesDraft}
                    bevelAutoFilled={targetBevelAutoFilled}
                    onChange={setTargetValuesDraft}
                    className="mt-3"
                  />
                </div>
              )}

              {settingsTab === 'limits' && (
                <div className="space-y-3">
                  <p className="text-xs text-heritage-grey">
                    Set an acceptable numeric range for each bevel measurement. Values below the minimum are highlighted in amber; values above the maximum in red. New welds start from sensible defaults. Leave only minimum or only maximum blank for a one-sided check.
                  </p>
                  {bevelRowLabels.map((row) => {
                    const rule = limitSettingsDraft[row] ?? { min: '', max: '', unit: 'in' as const }
                    return (
                      <div key={`limit-${row}`} className="rounded-lg border border-[#E2E5EA] bg-page-bg p-3">
                        <p className="text-[11px] font-bold uppercase tracking-wider text-heritage-grey">{row}</p>
                        <div className="mt-2 grid gap-3 sm:grid-cols-3">
                          <div>
                            <label className="text-[11px] font-medium text-heritage-grey">Minimum (in range)</label>
                            <input
                              className="input-primary mt-1"
                              type="number"
                              value={rule.min}
                              onChange={(event) =>
                                setLimitSettingsDraft((prev) => ({
                                  ...prev,
                                  [row]: { ...prev[row], min: event.target.value }
                                }))
                              }
                              placeholder="Optional"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-medium text-heritage-grey">Maximum (in range)</label>
                            <input
                              className="input-primary mt-1"
                              type="number"
                              value={rule.max}
                              onChange={(event) =>
                                setLimitSettingsDraft((prev) => ({
                                  ...prev,
                                  [row]: { ...prev[row], max: event.target.value }
                                }))
                              }
                              placeholder="Optional"
                            />
                          </div>
                          <div>
                            <label className="text-[11px] font-medium text-heritage-grey">Unit</label>
                            <InlineSelect
                              value={rule.unit}
                              options={[
                                { value: 'in', label: 'in' },
                                { value: 'mm', label: 'mm' }
                              ]}
                              onChange={(value) =>
                                setLimitSettingsDraft((prev) => ({
                                  ...prev,
                                  [row]: { ...prev[row], unit: value as 'in' | 'mm' }
                                }))
                              }
                              className="mt-1 w-20"
                              buttonClassName="px-2 py-1 text-xs justify-between"
                            />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {settingsTab === 'slope' && (
                <div className="space-y-3" data-testid="settings-slope-panel">
                  
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label
                        htmlFor="slope-in-input"
                        className="text-[11px] font-medium text-heritage-grey"
                      >
                        In (sec)
                      </label>
                      <input
                        id="slope-in-input"
                        data-testid="slope-in-input"
                        className="input-primary mt-1 w-full"
                        type="number"
                        min={0}
                        step={1}
                        value={slopeTimeDraft.inSec}
                        onChange={(event) =>
                          setSlopeTimeDraft((prev) => ({ ...prev, inSec: event.target.value }))
                        }
                        placeholder={String(DEFAULT_SLOPE_TIME.inSec)}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="slope-out-input"
                        className="text-[11px] font-medium text-heritage-grey"
                      >
                        Out (sec)
                      </label>
                      <input
                        id="slope-out-input"
                        data-testid="slope-out-input"
                        className="input-primary mt-1 w-full"
                        type="number"
                        min={0}
                        step={1}
                        value={slopeTimeDraft.outSec}
                        onChange={(event) =>
                          setSlopeTimeDraft((prev) => ({ ...prev, outSec: event.target.value }))
                        }
                        placeholder={String(DEFAULT_SLOPE_TIME.outSec)}
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-heritage-grey">
                    Leave at <strong>0</strong> to keep every sample. Defaults to{' '}
                    {DEFAULT_SLOPE_TIME.inSec}s in / {DEFAULT_SLOPE_TIME.outSec}s out on new welds.
                    Trimming uses the machine weld clock from TXT <strong>Start Time</strong>,{' '}
                    <strong>Time</strong>, and <strong>Duration</strong>.
                  </p>
                </div>
              )}
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <Button variant="secondary" type="button" onClick={() => setSettingsWeldId(null)}>
                Cancel
              </Button>
              <Button type="button" loading={savingSettings} disabled={savingSettings} onClick={() => void handleSaveSettings()}>
                Save
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {uploadDialogWeldId && id && pipeId ? (
        <UnifiedWeldUploadDialog
          open
          onClose={() => {
            setUploadDialogWeldId(null)
            setUploadDialogBevelIndex(null)
          }}
          projectId={id}
          pipeSetId={pipeId}
          weldId={uploadDialogWeldId}
          floorSheetWeldLabel={
            welds.find((w) => w.id === uploadDialogWeldId)?.weldId?.trim() ||
            welds.find((w) => w.id === uploadDialogWeldId)?.weldNumber?.trim() ||
            ''
          }
          currentWeldNumber={
            welds.find((w) => w.id === uploadDialogWeldId)?.weldId?.trim() ||
            welds.find((w) => w.id === uploadDialogWeldId)?.weldNumber?.trim() ||
            ''
          }
          wpsPassLabels={uploadDialogPassLabels}
          existingSummary={uploadDialogExistingSummary}
          weldIdSelectOptions={weldIdSelectOptions}
          onWeldIdChange={(value) => {
            if (uploadDialogWeldId) void applyWeldIdToRow(uploadDialogWeldId, value, { mode: 'local' })
          }}
          onCreateWeldId={async (bare) => {
            if (!uploadDialogWeldId || !project?.id) return ''
            const trimmed = bare.trim()
            if (!trimmed) return ''
            await addLabel('floor_weld_id', trimmed, project.id)
            void applyWeldIdToRow(uploadDialogWeldId, trimmed, { mode: 'local' })
            return trimmed
          }}
          slopeTime={normalizeSlopeTime(
            welds.find((w) => w.id === uploadDialogWeldId)?.slopeTime
          )}
          onApplyWeldId={(rowId, label) => {
            void applyWeldIdToRow(rowId, label, { mode: 'local' })
          }}
          onCreateWeldWithId={createWeldWithDetectedId}
          onPrepareBatch={async (groups, cwCcwMode, uploadFormat, telemetryMachineType) => {
            if (!id || !pipeId) return []
            const weldIds = Array.from(groups.keys()).sort()
            const { entries, nextWelds } = createWeldsForAlxBatch(weldIds)
            batchWeldsRef.current = { nextWelds, cwCcwMode }
            setWelds(nextWelds)
            const drafts = []
            for (const entry of entries) {
              const folderFiles = groups.get(entry.weldId.trim()) ?? []
              if (folderFiles.length === 0) continue
              if (uploadFormat === 'telemetry-txt') {
                const machineType = telemetryMachineType ?? 'p600z'
                const prepared = await prepareTelemetryUploadForFiles({
                  files: folderFiles,
                  weldIdLabel: entry.weldId.trim(),
                  machineType,
                  wpsPassLabels: uploadDialogPassLabels
                })
                drafts.push(
                  telemetryDraftFromPrepared({
                    rowId: entry.id,
                    weldIdLabel: entry.weldId.trim(),
                    files: folderFiles,
                    prepared,
                    machineType
                  })
                )
              } else {
                const prepared = await prepareAlxUploadForFiles({
                  files: folderFiles,
                  cwCcwMode,
                  wpsPassLabels: uploadDialogPassLabels
                })
                drafts.push({
                  rowId: entry.id,
                  weldIdLabel: entry.weldId.trim(),
                  files: folderFiles,
                  rows: prepared.rows,
                  passMappings: prepared.passMappings,
                  learnedSuggestions: prepared.learnedSuggestions,
                  sourceFilenames: prepared.sourceFilenames
                })
              }
            }
            return drafts
          }}
          onFinalizeBatch={async (drafts, uploadFormat) => {
            if (!id || !pipeId) return
            const batchCtx = batchWeldsRef.current
            const nextWelds = batchCtx?.nextWelds ?? welds
            const batchCwCcwMode = batchCtx?.cwCcwMode ?? 'merged'
            for (const draft of drafts) {
              if (uploadFormat === 'telemetry-txt') {
                const prepared = await prepareTelemetryUploadForFiles({
                  files: draft.files,
                  weldIdLabel: draft.weldIdLabel,
                  machineType: draft.machineType ?? 'p600z',
                  wpsPassLabels: uploadDialogPassLabels
                })
                const result = await finalizeTelemetryUploadForFiles({
                  projectId: id,
                  pipeSetId: pipeId,
                  weldRowId: draft.rowId,
                  files: draft.files,
                  weldIdLabel: draft.weldIdLabel,
                  machineType: draft.machineType ?? 'p600z',
                  passMappings: draft.passMappings,
                  filteredSamples: prepared.filteredSamples
                })
                const section = buildTelemetryWeldParamsSection(result)
                await updateSection(`weldparams.${draft.rowId}`, {
                  source: 'upload',
                  uploadFormat: 'telemetry-txt',
                  aggregate: section.aggregate,
                  sourceFilename: section.sourceFilename,
                  sourceFilenames: section.sourceFilenames,
                  passMappings: section.passMappings,
                  uploadedAt: section.uploadedAt
                })
              } else {
                const result = await finalizeAlxUploadForFiles({
                  projectId: id,
                  pipeSetId: pipeId,
                  weldRowId: draft.rowId,
                  files: draft.files,
                  rows: draft.rows,
                  passMappings: draft.passMappings,
                  cwCcwMode: batchCwCcwMode
                })
                const section = buildAlxWeldParamsSection(result, batchCwCcwMode)
                await updateSection(`weldparams.${draft.rowId}`, {
                  source: 'upload',
                  uploadFormat: 'alx-summary',
                  aggregate: section.aggregate,
                  sourceFilename: section.sourceFilename,
                  sourceFilenames: section.sourceFilenames,
                  passMappings: section.passMappings,
                  uploadedAt: section.uploadedAt,
                  cwCcwMode: batchCwCcwMode
                })
              }
            }
            const existingFloor = getSection('floorsheet')
            await updateSection('floorsheet', {
              ...(isFloorsheetSectionRecord(existingFloor) ? existingFloor : {}),
              preheatDefaultsPurged: true,
              welds: weldsForFloorsheetPersistence(nextWelds)
            })
            setSavedWeldsJson(JSON.stringify(nextWelds))
            batchWeldsRef.current = null
            setUploadDialogWeldId(null)
            setUploadDialogBevelIndex(null)
            navigate(`/projects/${id}/next/${pipeId}/floorsheet`)
          }}
          onUploadComplete={async (payload) => {
            const wid = uploadDialogWeldId
            const fmt = payload.uploadFormat ?? 'telemetry-txt'
            const existingFloor = getSection('floorsheet')
            await updateSection(`weldparams.${wid}`, {
              source: 'upload',
              uploadFormat: fmt,
              aggregate: payload.aggregate,
              sourceFilename: payload.sourceFilename,
              sourceFilenames: payload.sourceFilenames,
              passMappings: payload.passMappings,
              uploadedAt: payload.uploadedAt,
              ...(fmt === 'alx-summary' ? { cwCcwMode: 'merged' as CwCcwMode } : {})
            })
            await updateSection('floorsheet', {
              ...(isFloorsheetSectionRecord(existingFloor) ? existingFloor : {}),
              preheatDefaultsPurged: true,
              welds: weldsForFloorsheetPersistence(welds)
            })
            setSavedWeldsJson(JSON.stringify(welds))
            setUploadDialogWeldId(null)
            setUploadDialogBevelIndex(null)
            navigate(`/projects/${id}/next/${pipeId}/floorsheet/${wid}/parameters`)
          }}
        />
      ) : null}
      {data360RenameDialog && id && pipeId ? (
        <Data360PassMappingDialog
          open
          onClose={() => setData360RenameDialog(null)}
          projectId={id}
          pipeSetId={pipeId}
          weldRowId={data360RenameDialog.weldRowId}
          weldLabel={
            welds.find((w) => w.id === data360RenameDialog.weldRowId)?.weldId?.trim() ||
            welds.find((w) => w.id === data360RenameDialog.weldRowId)?.weldNumber?.trim() ||
            ''
          }
          mappingRows={getData360PassMappingRows(getSection, data360RenameDialog.weldRowId)}
          wpsPassLabels={passLabelsForBevel(
            welds.find((w) => w.id === data360RenameDialog.weldRowId)?.bevelIndex ?? null
          )}
          onComplete={() => {
            const weldRowId = data360RenameDialog.weldRowId
            setData360RenameDialog(null)
            void proceedAfterAutoSave({
              kind: 'navigate',
              to: `/projects/${id}/next/${pipeId}/floorsheet/${weldRowId}/parameters`
            })
          }}
        />
      ) : null}
      <ConfirmDialog
        open={Boolean(rootEquipmentChangeDialog)}
        title="Change root equipment?"
        description={
          rootEquipmentChangeDialog
            ? rootEquipmentChangeDialog.pending === 'iwm_and_rc'
              ? `Switch to ${rootEquipmentRadioLabel('iwm_and_rc')}? This will show the Root Misfire Record instead of the Root Bead Record.`
              : `Switch to ${rootEquipmentRadioLabel('iwm')}? This will show the Root Bead Record instead of the Root Misfire Record.`
            : ''
        }
        confirmLabel="Confirm"
        onConfirm={() => {
          if (!rootEquipmentChangeDialog) return
          const { weldId, pending: rootEquipment } = rootEquipmentChangeDialog
          setWelds((prev) =>
            prev.map((item) => {
              if (item.id !== weldId) return item
              const rows = syncOperatorRowsForRootEquipment(item.operators.rows, rootEquipment)
              return {
                ...item,
                rootEquipment,
                operators: {
                  ...item.operators,
                  rows,
                  equipment: equipmentListFromRows(rows),
                  operatorMap: syncOperatorMapFromRows(rows)
                }
              }
            })
          )
          setActivePassByWeld((prev) => ({
            ...prev,
            [weldId]:
              rootEquipment === 'iwm_and_rc' ? ROOT_MISFIRE_TAB : ROOT_BEAD_TAB
          }))
          setRootEquipmentChangeDialog(null)
        }}
        onCancel={() => setRootEquipmentChangeDialog(null)}
      />
      <ConfirmDialog
        open={Boolean(importDialog)}
        title={importDialog?.replaceExisting ? `Replace weld data from ${DATA_360_DISPLAY_NAME}?` : `Import weld data from ${DATA_360_DISPLAY_NAME}?`}
        description={
          importDialog?.replaceExisting
            ? `This weld already has parameters or telemetry. Importing will replace existing data for "${importDialog.weldName}".`
            : `Import parameters and telemetry for "${importDialog?.weldName ?? ''}" from ${DATA_360_DISPLAY_NAME} production data.`
        }
        confirmLabel={importDialog?.replaceExisting ? 'Replace' : 'Import'}
        confirmBusy={Boolean(importDialog && syncingWeldRowId === importDialog.weldRowId)}
        confirmBusyLabel={importDialog?.replaceExisting ? 'Replacing…' : 'Importing…'}
        onConfirm={() => void confirmData360Import()}
        onCancel={() => {
          if (syncingWeldRowId) return
          setImportDialog(null)
        }}
      />
    </div>
  )
}
